import React, { useState, useEffect } from "react";
import { Plus, Edit, Check, X, Building, Shield, CalendarDays, Clock, Users, CheckCircle, DollarSign, Upload, AlertTriangle, Award, ShieldCheck, Wallet, Package, Trash2, ArrowUp, ArrowDown, HelpCircle } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Tbl, Stat, Sec, KpiStrip } from "../components";
import { FreezeWindowConfig } from "../config/FreezeWindowConfig";
import { FormModal } from "../components/FormModal";
import { notify, confirmDialog, promptDialog } from "../notify";
import { SalaryStructureManager } from "../components/SalaryStructureManager";
import { PermissionsManager } from "../components/PermissionsManager";
import { useHashParam } from "../utils/hashState";

const LeaveTypeModal = ({ item, onSave, onClose }) => {
  const isNew = !!item._new;
  const [fv, setFv] = useState(isNew ? { accrual: "yearly", paid: "true", requires_approval: "true", is_unlimited: "false", carry_forward: "false", return_required: "false", document_required: "false", color: "#5F6CF0" } : {
    code: item.cd, name: item.nm, default_balance: String(item.bal || 0), is_unlimited: String(item.unl || false), requires_approval: String(item.req_apr !== false), gender_specific: item.gen || "", carry_forward: String(item.cf || false), max_consecutive_days: String(item.mx || 0), color: item.clr || "#5F6CF0", accrual: item.accrual || "yearly", monthly_credit: String(item.mc || 0), paid: String(item.paid !== false), min_tenure_days: String(item.tenure || 0), description: item.desc || "", return_required: String(item.ret || false), document_required: String(item.docReq || false)
  });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
  const doSave = async () => { if (!fv.name) { setErr("Name is required"); return; } if (isNew && !fv.code) { setErr("Code is required"); return; } setSaving(true); setErr(""); try { await onSave(fv); notify(isNew ? "Leave type created" : "Leave type updated", { title: "Saved", variant: "success" }); onClose(); } catch (e) { setErr(e.message || "Error"); notify(e.message || "Failed to save leave type", { title: "Error", variant: "error" }); } finally { setSaving(false); } };
  const ff = (label, k, type, options, show, placeholder) => { if (show === false) return null; return <div key={k} className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">{label}</div>{type === "select" ? <Sl options={options} value={fv[k] || ""} onChange={v => setFv(p => ({ ...p, [k]: v }))} /> : <Inp placeholder={placeholder || ""} value={fv[k] || ""} onChange={v => setFv(p => ({ ...p, [k]: v }))} type={type} />}</div>; };
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[824px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">{isNew ? "Add Leave Type" : "Edit: " + item.nm}</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px]">
        {isNew && ff("Code", "code", null, null, true, "ML")}
        {!isNew && <div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Code (read-only)</div><div className="px-[10px] py-[7px] bg-bg2 rounded-lg text-[14px] text-t3 border border-b0">{item.cd}</div></div>}
        {ff("Name", "name", null, null, true, "Maternity Leave")}
        {ff("Default Balance (days)", "default_balance", "number", null, true, "12")}
        {ff("Accrual Period", "accrual", "select", [{ v: "yearly", l: "Yearly" }, { v: "monthly", l: "Monthly" }, { v: "one_time", l: "One-Time" }])}
        {ff("Monthly Credit (days/month)", "monthly_credit", "number", null, fv.accrual === "monthly", "1.0")}
        {ff("Is Unlimited", "is_unlimited", "select", [{ v: "false", l: "No" }, { v: "true", l: "Yes" }])}
        {ff("Requires Approval", "requires_approval", "select", [{ v: "true", l: "Yes" }, { v: "false", l: "No" }])}
        {ff("Gender Specific", "gender_specific", "select", [{ v: "", l: "All Genders" }, { v: "F", l: "Female Only" }, { v: "M", l: "Male Only" }])}
        {ff("Carry Forward", "carry_forward", "select", [{ v: "false", l: "No" }, { v: "true", l: "Yes" }])}
        {ff("Max Consecutive Days", "max_consecutive_days", "number", null, true, "7")}
        {ff("Paid Leave", "paid", "select", [{ v: "true", l: "Paid" }, { v: "false", l: "Unpaid" }])}
        {ff("Track Return Date", "return_required", "select", [{ v: "false", l: "No" }, { v: "true", l: "Yes — auto-calculate return date" }])}
        {ff("Document Required", "document_required", "select", [{ v: "false", l: "No" }, { v: "true", l: "Yes — employee must upload document" }])}
        {ff("Min Tenure (days)", "min_tenure_days", "number", null, true, "0")}
        {ff("Color (hex)", "color", null, null, true, "#F078A2")}
        <div className="flex items-center gap-2 mb-[10px]"><div className="text-[14px] text-t2">Preview:</div><div className="w-6 h-6 rounded-md" style={{ background: fv.color || "#5F6CF0" }} /><Bg c={fv.color || "#5F6CF0"}>{fv.code || fv.name || "Type"}</Bg></div>
      </div>
      <div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Description</div><textarea value={fv.description || ""} onChange={e => setFv(p => ({ ...p, description: e.target.value }))} placeholder="Describe this leave type..." className="w-full min-h-[50px] p-2 bg-bg2 border border-b0 rounded-lg text-t0 text-[14px] outline-none resize-y box-border" /></div>
      {fv.return_required === "true" && <div className="mb-[10px] p-[10px] rounded-[10px]" style={{ background: `${K.pu}08`, border: `1px solid ${K.pu}22` }}><div className="flex items-center gap-[6px]"><AlertTriangle size={12} color={K.pu} /><span className="text-[14px] font-semibold text-pu">Return Tracking Enabled</span></div><div className="text-[14px] text-t2 mt-1">When approved, the system calculates the expected return date (start + {fv.default_balance || 0} days) and displays it on the leave detail view.</div></div>}
      {err && <div className="text-[14px] text-r mb-2">{err}</div>}
      <div className="flex gap-[6px] mt-3"><Bt full onClick={doSave} disabled={saving}>{saving ? "Saving..." : isNew ? "Create Leave Type" : "Save Changes"}</Bt><Bt v="secondary" full onClick={onClose}>Cancel</Bt></div>
    </div>
  </div>;
};

// Separation settings tab — three editors:
//   1) FnF / lifecycle config (notice period, salary divisor, encashable
//      leave types, gratuity formula + min years, FnF due window,
//      separation_types and reason_categories CSV-style chips).
//   2) Checklist templates editor — per separation_type rows; '*' is
//      the universal default.
//   3) Asset templates editor — same shape, fewer fields.
//
// Backend gates writes to admin/hr_head, but we also hide the tab's
// editors for centre_head + below to keep the UX honest.
const SeparationSettingsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [chk, setChk] = useState([]);
  const [ast, setAst] = useState([]);
  const [section, setSection] = useState('config'); // config | checklist | asset
  const [savedMsg, setSavedMsg] = useState('');

  const reload = async () => {
    try {
      const [c, k, a] = await Promise.all([
        api.get('/api/separation/config'),
        api.get('/api/separation/templates/checklist'),
        api.get('/api/separation/templates/asset'),
      ]);
      setCfg(c); setChk(k || []); setAst(a || []);
    } catch (e) { setSavedMsg('Error: ' + (e.message || 'Load failed')); }
  };
  React.useEffect(() => { reload(); }, []);

  const flash = (msg, isErr = false) => { setSavedMsg((isErr ? 'Error: ' : '') + msg); setTimeout(() => setSavedMsg(''), 3500); };

  const saveCfg = async (patch) => {
    try { const r = await api.put('/api/separation/config', patch); setCfg(r); flash('Config saved'); notify('Separation config saved', { title: 'Saved', variant: 'success' }); }
    catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save separation config', { title: 'Error', variant: 'error' }); }
  };

  const SectBtn = ({ k, l }) => (
    <button onClick={() => setSection(k)}
            className={`px-3 py-[5px] text-[14px] cursor-pointer rounded-lg ${section === k ? 'bg-pg text-p font-semibold' : 'bg-transparent text-t3'}`}>{l}</button>
  );

  if (!cfg) return <div className="text-[14px] text-t3 p-3">Loading separation settings…</div>;

  return <div>
    {savedMsg && <div className={`px-3 py-2 mb-[10px] rounded-lg text-[14px] font-semibold ${savedMsg.startsWith('Error') ? 'bg-rb text-r border border-rd' : 'bg-gb text-g border border-gd'}`}>{savedMsg}</div>}
    <div className="flex gap-1 mb-3">
      <SectBtn k="config"    l={`FnF & Defaults`} />
      <SectBtn k="checklist" l={`Checklist Templates (${chk.length})`} />
      <SectBtn k="asset"     l={`Asset Templates (${ast.length})`} />
    </div>

    {section === 'config' && <SeparationConfigEditor cfg={cfg} onSave={saveCfg} />}
    {section === 'checklist' && <SeparationChecklistTemplates rows={chk} reload={reload} flash={flash} />}
    {section === 'asset'     && <SeparationAssetTemplates     rows={ast} reload={reload} flash={flash} />}
  </div>;
};

// --- FnF + lifecycle config form. Stages every change locally; only
// commits on Save so a stray click doesn't push partial state.
const SeparationConfigEditor = ({ cfg, onSave }) => {
  const [v, setV] = useState({
    notice_period_default: cfg.notice_period_default ?? 30,
    days_after_lwd: cfg.days_after_lwd ?? 45,
    salary_days_per_month: cfg.salary_days_per_month ?? 30,
    gratuity_min_years: cfg.gratuity_min_years ?? cfg.gratuity_years ?? 5,
    gratuity_formula: cfg.gratuity_formula ?? 'india_payment_act',
    notice_buyout_allowed: cfg.notice_buyout_allowed !== false,
    encashable_leave_types: (cfg.encashable_leave_types || ['EL','CL']).join(', '),
    separation_types: (cfg.separation_types || []).join(', '),
    reason_categories: (cfg.reason_categories || []).join(', '),
  });
  const set = (k, x) => setV(p => ({ ...p, [k]: x }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        notice_period_default: parseInt(v.notice_period_default) || 30,
        days_after_lwd: parseInt(v.days_after_lwd) || 45,
        salary_days_per_month: parseInt(v.salary_days_per_month) || 30,
        gratuity_min_years: parseInt(v.gratuity_min_years) || 5,
        gratuity_formula: v.gratuity_formula,
        notice_buyout_allowed: !!v.notice_buyout_allowed,
        encashable_leave_types: v.encashable_leave_types.split(',').map(s => s.trim()).filter(Boolean),
        separation_types: v.separation_types.split(',').map(s => s.trim()).filter(Boolean),
        reason_categories: v.reason_categories.split(',').map(s => s.trim()).filter(Boolean),
      });
    } finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return <Cd>
    <div className="text-[14px] font-bold text-t0 mb-1">FnF Settlement & Lifecycle Defaults</div>
    <div className="text-[14px] text-t3 mb-3">Per-employee values (e.g. notice period on the contract) override these company defaults.</div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 mb-3">
      <div>
        <div className={lbl}>Default Notice Period (days)</div>
        <Inp type="number" value={String(v.notice_period_default)} onChange={x => set('notice_period_default', x)} />
      </div>
      <div>
        <div className={lbl}>FnF due — days after LWD</div>
        <Inp type="number" value={String(v.days_after_lwd)} onChange={x => set('days_after_lwd', x)} />
      </div>

      <div>
        <div className={lbl}>Salary days / month (divisor for daily pay)</div>
        <Inp type="number" value={String(v.salary_days_per_month)} onChange={x => set('salary_days_per_month', x)} />
        <div className="text-[12px] text-t3 mt-[2px]">30 = calendar; 26 = working-day basis</div>
      </div>
      <div>
        <div className={lbl}>Gratuity — minimum years of service</div>
        <Inp type="number" value={String(v.gratuity_min_years)} onChange={x => set('gratuity_min_years', x)} />
      </div>

      <div>
        <div className={lbl}>Gratuity formula</div>
        <Sl options={[
          { v: 'india_payment_act',   l: 'India Payment of Gratuity Act — (15/26) × salary × years' },
          { v: 'one_month_per_year',  l: 'One month per year of service' },
          { v: 'half_month_per_year', l: 'Half month per year of service' },
        ]} value={v.gratuity_formula} onChange={x => set('gratuity_formula', x)} />
      </div>
      <div>
        <div className={lbl}>Notice buyout allowed?</div>
        <Sl options={[{ v: 'true', l: 'Yes — employees may buy out remaining notice' }, { v: 'false', l: 'No' }]}
            value={v.notice_buyout_allowed ? 'true' : 'false'}
            onChange={x => set('notice_buyout_allowed', x === 'true')} />
      </div>

      <div className="md:col-span-2">
        <div className={lbl}>Encashable leave types (comma-separated codes, e.g. EL, CL)</div>
        <Inp value={v.encashable_leave_types} onChange={x => set('encashable_leave_types', x)} placeholder="EL, CL" />
      </div>
      <div className="md:col-span-2">
        <div className={lbl}>Separation types (comma-separated)</div>
        <Inp value={v.separation_types} onChange={x => set('separation_types', x)} placeholder="Resignation, Termination, …" />
      </div>
      <div className="md:col-span-2">
        <div className={lbl}>Reason categories (comma-separated)</div>
        <Inp value={v.reason_categories} onChange={x => set('reason_categories', x)} placeholder="Better Opportunity, Personal, …" />
      </div>
    </div>

    <Bt s="sm" icon={Check} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Config'}</Bt>
  </Cd>;
};

// --- Checklist templates editor: list + inline add/edit/delete.
const SeparationChecklistTemplates = ({ rows, reload, flash }) => {
  const [edit, setEdit] = useState(null);
  const [delBusy, setDelBusy] = useState(null);

  const save = async (vals) => {
    try {
      if (edit?.id) {
        await api.put(`/api/separation/templates/checklist/${edit.id}`, {
          separation_type: vals.separation_type, category: vals.category,
          item_name: vals.item_name, description: vals.description,
          sort_order: parseInt(vals.sort_order) || 0,
          is_active: vals.is_active !== 'false',
        });
        flash('Saved');
        notify('Checklist item saved', { title: 'Saved', variant: 'success' });
      } else {
        await api.post('/api/separation/templates/checklist', {
          separation_type: vals.separation_type || '*',
          category: vals.category, item_name: vals.item_name,
          description: vals.description,
          sort_order: parseInt(vals.sort_order) || 0,
        });
        flash('Created');
        notify('Checklist item added', { title: 'Added', variant: 'success' });
      }
      setEdit(null); reload();
    } catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save checklist item', { title: 'Error', variant: 'error' }); throw e; }
  };

  const del = async (r) => {
    if (!(await confirmDialog(`Delete "${r.item_name}"? Existing separations are unaffected — only future ones change.`))) return;
    setDelBusy(r.id);
    try { await api.del(`/api/separation/templates/checklist/${r.id}`); flash('Deleted'); notify('Checklist item deleted', { title: 'Deleted', variant: 'success' }); reload(); }
    catch (e) { flash(e.message || 'Delete failed', true); notify(e.message || 'Failed to delete checklist item', { title: 'Error', variant: 'error' }); }
    finally { setDelBusy(null); }
  };

  return <Cd>
    <div className="flex justify-between items-center mb-2">
      <div className="text-[14px] font-bold text-t0">Checklist Templates</div>
      <Bt icon={Plus} s="sm" onClick={() => setEdit({ _new: true })}>Add Item</Bt>
    </div>
    <div className="text-[14px] text-t3 mb-2">
      <span className="font-mono bg-bg2 px-1 rounded">*</span> applies to all separation types. Add a type-specific row (e.g. <span className="font-mono">Termination</span>) to override per type.
    </div>
    <Tbl cols={[
      { k: 'type', l: 'Type', r: r => <Bg c={r.separation_type === '*' ? K.t3 : K.bl} sm>{r.separation_type}</Bg> },
      { k: 'cat',  l: 'Category', r: r => <span className="text-[15px] font-bold text-t0">{r.category}</span> },
      { k: 'item', l: 'Item', r: r => <span className="text-[14px]">{r.item_name}</span> },
      { k: 'desc', l: 'Description', r: r => <span className="text-[12px] text-t3 truncate max-w-[220px] inline-block">{r.description || '—'}</span> },
      { k: 'ord',  l: 'Sort', r: r => <span className="text-[14px] text-t3">{r.sort_order}</span> },
      { k: 'act',  l: 'Status', r: r => <Bg c={r.is_active ? K.g : K.t3} sm dot>{r.is_active ? 'Active' : 'Inactive'}</Bg> },
      { k: 'a',    l: '', r: r => <div className="flex gap-1">
        <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEdit(r)}>Edit</Bt>
        <Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(r)} disabled={delBusy === r.id}>{delBusy === r.id ? 'Deleting…' : 'Delete'}</Bt>
      </div> },
    ]} data={rows} />
    {rows.length === 0 && <div className="text-center text-[14px] text-t3 py-4">No templates yet</div>}

    {edit && <FormModal title={edit._new ? 'Add Checklist Item' : 'Edit Checklist Item'}
      fields={[
        { key: 'separation_type', label: 'Separation Type ("*" = all types)', placeholder: '*' },
        { key: 'category', label: 'Category', placeholder: 'Employee / Manager / HR / IT / Finance / Admin / Legal' },
        { key: 'item_name', label: 'Item', placeholder: 'Resignation letter submitted' },
        { key: 'description', label: 'Description', placeholder: 'What does this step involve?' },
        { key: 'sort_order', label: 'Sort Order', type: 'number' },
        ...(edit._new ? [] : [{ key: 'is_active', label: 'Active', type: 'select', options: [{ v: 'true', l: 'Active' }, { v: 'false', l: 'Inactive' }] }]),
      ]}
      initialValues={edit._new ? { separation_type: '*', sort_order: '0' }
                                : { separation_type: edit.separation_type, category: edit.category, item_name: edit.item_name, description: edit.description || '', sort_order: edit.sort_order, is_active: edit.is_active ? 'true' : 'false' }}
      onSubmit={save}
      onClose={() => setEdit(null)} />}
  </Cd>;
};

// --- Asset templates editor: same shape, fewer fields.
const SeparationAssetTemplates = ({ rows, reload, flash }) => {
  const [edit, setEdit] = useState(null);
  const [delBusy, setDelBusy] = useState(null);

  const save = async (vals) => {
    try {
      if (edit?.id) {
        await api.put(`/api/separation/templates/asset/${edit.id}`, {
          separation_type: vals.separation_type,
          asset_type: vals.asset_type,
          asset_description: vals.asset_description,
          sort_order: parseInt(vals.sort_order) || 0,
          is_active: vals.is_active !== 'false',
        });
        flash('Saved');
        notify('Asset template saved', { title: 'Saved', variant: 'success' });
      } else {
        await api.post('/api/separation/templates/asset', {
          separation_type: vals.separation_type || '*',
          asset_type: vals.asset_type,
          asset_description: vals.asset_description,
          sort_order: parseInt(vals.sort_order) || 0,
        });
        flash('Created');
        notify('Asset template added', { title: 'Added', variant: 'success' });
      }
      setEdit(null); reload();
    } catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save asset template', { title: 'Error', variant: 'error' }); throw e; }
  };

  const del = async (r) => {
    if (!(await confirmDialog(`Delete "${r.asset_type}"? Existing separations are unaffected.`))) return;
    setDelBusy(r.id);
    try { await api.del(`/api/separation/templates/asset/${r.id}`); flash('Deleted'); notify('Asset template deleted', { title: 'Deleted', variant: 'success' }); reload(); }
    catch (e) { flash(e.message || 'Delete failed', true); notify(e.message || 'Failed to delete asset template', { title: 'Error', variant: 'error' }); }
    finally { setDelBusy(null); }
  };

  return <Cd>
    <div className="flex justify-between items-center mb-2">
      <div className="text-[14px] font-bold text-t0">Asset Templates</div>
      <Bt icon={Plus} s="sm" onClick={() => setEdit({ _new: true })}>Add Item</Bt>
    </div>
    <div className="text-[14px] text-t3 mb-2">
      Items HR collects on exit but the core asset module doesn't track (uniforms, parking pass, keys, books). Tracked items come from <span className="font-mono">asset_assignments</span> automatically — don't duplicate them here.
    </div>
    <Tbl cols={[
      { k: 'type', l: 'Sep Type', r: r => <Bg c={r.separation_type === '*' ? K.t3 : K.bl} sm>{r.separation_type}</Bg> },
      { k: 'asset', l: 'Asset Type', r: r => <span className="text-[15px] font-bold text-t0">{r.asset_type}</span> },
      { k: 'desc',  l: 'Description', r: r => <span className="text-[12px] text-t3">{r.asset_description || '—'}</span> },
      { k: 'ord',   l: 'Sort', r: r => <span className="text-[14px] text-t3">{r.sort_order}</span> },
      { k: 'act',   l: 'Status', r: r => <Bg c={r.is_active ? K.g : K.t3} sm dot>{r.is_active ? 'Active' : 'Inactive'}</Bg> },
      { k: 'a',     l: '', r: r => <div className="flex gap-1">
        <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEdit(r)}>Edit</Bt>
        <Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(r)} disabled={delBusy === r.id}>{delBusy === r.id ? 'Deleting…' : 'Delete'}</Bt>
      </div> },
    ]} data={rows} />
    {rows.length === 0 && <div className="text-center text-[14px] text-t3 py-4">No templates yet</div>}

    {edit && <FormModal title={edit._new ? 'Add Asset Item' : 'Edit Asset Item'}
      fields={[
        { key: 'separation_type', label: 'Separation Type ("*" = all types)', placeholder: '*' },
        { key: 'asset_type', label: 'Asset Type', placeholder: 'Uniform / Parking Pass / …' },
        { key: 'asset_description', label: 'Description' },
        { key: 'sort_order', label: 'Sort Order', type: 'number' },
        ...(edit._new ? [] : [{ key: 'is_active', label: 'Active', type: 'select', options: [{ v: 'true', l: 'Active' }, { v: 'false', l: 'Inactive' }] }]),
      ]}
      initialValues={edit._new ? { separation_type: '*', sort_order: '0' }
                                : { separation_type: edit.separation_type, asset_type: edit.asset_type, asset_description: edit.asset_description || '', sort_order: edit.sort_order, is_active: edit.is_active ? 'true' : 'false' }}
      onSubmit={save}
      onClose={() => setEdit(null)} />}
  </Cd>;
};

const RL_DEFAULT = [{id:"ceo",t:"CEO",lv:1,c:"#FFD700",la:999},{id:"coo",t:"COO",lv:2,c:"#F0BE2E",la:999},{id:"cto",t:"CTO",lv:2,c:"#5AA0F0",la:999},{id:"cfo",t:"CFO",lv:2,c:"#2BD49D",la:999},{id:"chro",t:"CHRO",lv:2,c:"#A378F0",la:999},{id:"vp",t:"VP",lv:3,c:"#F078A2",la:30},{id:"ch",t:"Centre Head",lv:3,c:"#F06268",la:20},{id:"dir",t:"Director",lv:4,c:"#5AA0F0",la:15},{id:"sm",t:"Sr Manager",lv:5,c:"#5F6CF0",la:10},{id:"mgr",t:"Manager",lv:5,c:"#2AD4BC",la:5},{id:"am",t:"Asst Manager",lv:6,c:"#7E89F4",la:3},{id:"tl",t:"Team Lead",lv:6,c:"#30D4EC",la:2},{id:"se",t:"Sr Engineer",lv:7,c:"#C5CBD8",la:0},{id:"eng",t:"Engineer",lv:7,c:"#C5CBD8",la:0},{id:"je",t:"Jr Engineer",lv:8,c:"#8A92A6",la:0},{id:"asc",t:"Associate",lv:8,c:"#8A92A6",la:0},{id:"int",t:"Intern",lv:9,c:"#5B6377",la:0},{id:"ea",t:"Exec Asst",lv:7,c:"#C5CBD8",la:0},{id:"rec",t:"Receptionist",lv:9,c:"#5B6377",la:0},{id:"ob",t:"Office Boy",lv:10,c:"#5B6377",la:0},{id:"sec",t:"Security",lv:10,c:"#5B6377",la:0},{id:"drv",t:"Driver",lv:10,c:"#5B6377",la:0},{id:"acc",t:"Accountant",lv:7,c:"#2BD49D",la:0},{id:"hre",t:"HR Exec",lv:7,c:"#A378F0",la:2},{id:"rcr",t:"Recruiter",lv:7,c:"#F0BE2E",la:0}];

// Per-company Payroll settings editor. Two sections:
//   1) FnF / lifecycle defaults (cycle, working days, salary structure %,
//      conveyance, medical, PF/ESI/PT rates and ceilings, TDS mode + slabs).
//   2) Bank file format default for this company.
// Inheritance: company can leave any field blank to inherit the global
// baseline. PUT strips empty values so unsetting a field reverts to global.
// TDS slab editor — table-style editor for progressive slabs. Each row
// has an upper bound (₹/year) and a rate (%); the last row's upper bound
// is left blank to mean "everything above". Stored as the JSONB array
// payroll_settings.tds_slabs and read by the slab-mode TDS calculator.
const TdsSlabEditor = ({ effectiveMode, slabs, inherited, onChange, onRevert }) => {
  const active = effectiveMode === 'slab';

  const setSlab = (idx, key, val) => {
    const next = [...slabs];
    next[idx] = { ...next[idx], [key]: key === 'up_to_annual' ? (val === '' ? null : Number(val)) : Number(val) };
    onChange(next);
  };
  const addSlab = () => onChange([...slabs, { up_to_annual: null, rate: 0 }]);
  const removeSlab = (idx) => onChange(slabs.filter((_, i) => i !== idx));

  return (
    <div className="border-t border-b0 pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[14px] font-bold text-t0">TDS Slabs (annual)</div>
          <div className="text-[12px] text-t3 mt-[1px]">
            Used when mode = "Progressive slab". {inherited && <span className="italic text-pu">Inherited from global default — edits create a company override.</span>}
            {!active && <span className="text-a italic"> Currently inactive (mode is "{effectiveMode}").</span>}
          </div>
        </div>
        <div className="flex gap-1">
          {!inherited && <Bt v="ghost" s="xs" icon={X} onClick={onRevert}>Revert to inherited</Bt>}
          <Bt v="secondary" s="xs" icon={Plus} onClick={addSlab}>Add slab</Bt>
        </div>
      </div>

      {slabs.length === 0 ? (
        <div className="text-[14px] text-t3 py-2">No slabs defined. Add at least one (or revert to the inherited default — Indian new regime).</div>
      ) : (
        <div className="space-y-1">
          {slabs.map((s, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="text-[14px] text-t3 w-[80px]">Slab {i + 1}</div>
              <div className="flex-1">
                <div className="text-[12px] text-t3 mb-[2px]">Up to ₹/year (blank = everything above)</div>
                <Inp type="number" placeholder="No upper limit" value={s.up_to_annual == null ? '' : String(s.up_to_annual)}
                     onChange={v => setSlab(i, 'up_to_annual', v)} />
              </div>
              <div className="w-[120px]">
                <div className="text-[12px] text-t3 mb-[2px]">Rate %</div>
                <Inp type="number" placeholder="0" value={String(s.rate ?? 0)} onChange={v => setSlab(i, 'rate', v)} />
              </div>
              <Bt v="ghost" s="xs" icon={Trash2} onClick={() => removeSlab(i)}>Remove</Bt>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Chip-style editor for the deduction_types enum. Add via Enter or the
// + button, remove via the × on each chip. Deduplicates + trims silently.
// Shared chip editor. Originally built for deduction types; now also
// used by the Recruitment Options card, so the placeholder + button
// label are caller-configurable.
const DeductionTypeChips = ({ types, onChange, placeholder = 'e.g. Uniform Recovery', addLabel = 'Add Type' }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    const existing = (types || []).map(t => t.toLowerCase());
    if (existing.includes(v.toLowerCase())) { setDraft(''); return; }
    onChange([...(types || []), v]);
    setDraft('');
  };

  const remove = (i) => {
    const next = [...(types || [])];
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2 min-h-[28px]">
        {(types || []).length === 0 && (
          <div className="text-[14px] text-t3 italic">No entries — add at least one below.</div>
        )}
        {(types || []).map((t, i) => (
          <span key={t + i} className="inline-flex items-center gap-1 pl-2 pr-1 py-[3px] bg-bg2 border border-b0 rounded-full text-[14px] text-t1">
            {t}
            <button
              onClick={() => remove(i)}
              className="w-4 h-4 rounded-full bg-transparent border-0 text-t3 cursor-pointer flex items-center justify-center hover:text-r"
              title="Remove"
            >×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none"
          placeholder={placeholder}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Bt icon={Plus} onClick={add} s="sm">{addLabel}</Bt>
      </div>
    </div>
  );
};

// Deduction Rules — org-wide thresholds + acknowledgment window + cap.
// Feeds the /api/deductions/{settings,POST /,approve,…} routes so HR knows
// where the boundaries between HR-only, Manager, CH, and CHRO/Admin sit,
// and can adjust the 48h auto-approve window or the 50%-of-net cap.
const DeductionRulesCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  React.useEffect(() => {
    api.get('/api/deductions/settings').then(setSettings).catch(() => setSettings({}));
  }, []);

  if (!settings) return null;
  const t = settings.approval_thresholds || {};

  const upd = (path, val) => {
    // Deep clone the branch we're editing; keep the rest of the object.
    setSettings(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = cur[parts[i]] || {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3000); };

  const save = async () => {
    setSaving(true);
    try {
      const clean = (n) => (n === '' || n == null) ? null : Number(n);
      const patch = {
        approval_thresholds: {
          hr_only:     { max: clean(t.hr_only?.max) },
          manager:     { max: clean(t.manager?.max) },
          centre_head: { max: clean(t.centre_head?.max) },
          chro_admin:  { max: null },
        },
        employee_ack_required_above:      clean(settings.employee_ack_required_above),
        employee_ack_auto_approve_hours:  clean(settings.employee_ack_auto_approve_hours),
        max_per_month_pct_of_net:         clean(settings.max_per_month_pct_of_net),
        deduction_types:                  Array.isArray(settings.deduction_types) ? settings.deduction_types : [],
      };
      const upd = await api.put('/api/deductions/settings', patch);
      setSettings(upd);
      flash('Saved');
      notify('Deduction rules saved', { title: 'Saved', variant: 'success' });
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to save deduction rules', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";

  return (
    <Cd style={{ marginBottom: 14 }}>
      <div className="text-[14px] font-bold text-t0 mb-1">Manual Deduction Rules</div>
      <div className="text-[14px] text-t3 mb-3">
        Amount bands decide who approves a deduction (asset loss, damage, advance recovery, …). Anything above the top band routes to CHRO / Admin. Employees must acknowledge deductions over the "ack threshold" or the request auto-approves after the timer.
      </div>
      <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mt-2 mb-2">Approval Ladder — Amount Bands (₹)</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <div className="text-[14px] text-t3 mb-1">HR alone up to (₹)</div>
          <input type="number" className={inputCls} value={t.hr_only?.max ?? ''} onChange={e => upd('approval_thresholds.hr_only.max', e.target.value)} />
        </div>
        <div>
          <div className="text-[14px] text-t3 mb-1">Manager up to (₹)</div>
          <input type="number" className={inputCls} value={t.manager?.max ?? ''} onChange={e => upd('approval_thresholds.manager.max', e.target.value)} />
        </div>
        <div>
          <div className="text-[14px] text-t3 mb-1">Centre Head up to (₹)</div>
          <input type="number" className={inputCls} value={t.centre_head?.max ?? ''} onChange={e => upd('approval_thresholds.centre_head.max', e.target.value)} />
        </div>
      </div>
      <div className="text-[14px] text-t3 mb-3">Anything above Centre Head routes to CHRO / Admin.</div>

      <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mt-2 mb-2">Employee Acknowledgment</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <div className="text-[14px] text-t3 mb-1">Ack required above (₹)</div>
          <input type="number" className={inputCls} value={settings.employee_ack_required_above ?? ''} onChange={e => upd('employee_ack_required_above', e.target.value)} />
        </div>
        <div>
          <div className="text-[14px] text-t3 mb-1">Auto-approve after (hours)</div>
          <input type="number" className={inputCls} value={settings.employee_ack_auto_approve_hours ?? ''} onChange={e => upd('employee_ack_auto_approve_hours', e.target.value)} />
        </div>
        <div>
          <div className="text-[14px] text-t3 mb-1">Max per month (% of net)</div>
          <input type="number" className={inputCls} value={settings.max_per_month_pct_of_net ?? ''} onChange={e => upd('max_per_month_pct_of_net', e.target.value)} />
        </div>
      </div>

      {/* Deduction Types — HR curates the enum HR Corner's Add form
           uses for the Type dropdown. Existing rows keep their type
           string even if HR removes it later; only new deductions are
           constrained to the current list. */}
      <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mt-2 mb-2">Deduction Types</div>
      <div className="text-[12px] text-t3 mb-2">
        These appear as the "Deduction Type" dropdown when HR raises a deduction. Add specific categories your org uses (e.g. "Uniform Recovery", "Training Bond Break").
      </div>
      <DeductionTypeChips
        types={settings.deduction_types || []}
        onChange={list => setSettings(prev => ({ ...prev, deduction_types: list }))}
      />
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-b0">
        <Bt icon={CheckCircle} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Rules'}</Bt>
        {msg && <div className={`text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
      </div>
    </Cd>
  );
};

// Recruitment dropdown lists — HR/admin can extend the enums that back
// the Add Candidate + Raise Hiring Request forms. Same chip-editor
// pattern as DeductionTypeChips so it feels familiar.
// Admin-controlled kill-switches for outbound email. SMTP creds still live
// in the server .env — this only governs which of the wired categories
// actually dispatch. Layered gates on the mailer:
//   1. .env SMTP_HOST/USER/PASS present     — physical ability to send
//   2. email_config.enabled                 — global kill-switch (this UI)
//   3. email_config.exit_docs.enabled       — category master (this UI)
//   4. email_config.exit_docs.per_type[X]   — per-document toggle (this UI)
// Every gate that skips writes an audit_log entry so HR sees exactly why.
const EmailSettingsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => api.get('/api/settings/email').then(setCfg).catch(() => setCfg({ enabled: true, exit_docs: { enabled: true, per_type: {} }, smtp: { configured: false } }));
  // Statement-body arrow — otherwise React stores the returned Promise
  // as a cleanup function and calls it on unmount, throwing
  // "n is not a function" and blanking the settings page when the
  // admin switches away from this tab.
  React.useEffect(() => { load(); }, []);
  if (!cfg) return null;

  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3000); };

  const save = async () => {
    setSaving(true);
    try {
      const upd = await api.put('/api/settings/email', {
        enabled: cfg.enabled,
        exit_docs: { enabled: cfg.exit_docs.enabled, per_type: cfg.exit_docs.per_type },
      });
      setCfg({ ...upd, smtp: cfg.smtp });
      flash('Saved');
      notify('Email settings saved', { title: 'Saved', variant: 'success' });
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to save email settings', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const Toggle = ({ checked, onChange, label, hint, disabled }) => (
    <label className={`flex items-start gap-3 py-2 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={e => onChange(e.target.checked)} className="mt-[3px] accent-p" />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-t0">{label}</div>
        {hint && <div className="text-[14px] text-t3 mt-[1px]">{hint}</div>}
      </div>
    </label>
  );

  const DOC_TYPES = [
    ['Resignation Letter',       'Sent when a resignation is initiated.'],
    ['Relieving Letter',         'Sent when separation moves to Exit In Progress.'],
    ['Exit Interview Form',      'Sent when the exit interview record is created.'],
    ['Full & Final Settlement',  'Sent when FnF is approved.'],
    ['Experience Letter (Exit)', 'Sent when the separation is marked Completed.'],
  ];

  const setPerType = (docType, on) => setCfg(prev => ({
    ...prev,
    exit_docs: {
      ...prev.exit_docs,
      per_type: { ...prev.exit_docs.per_type, [docType]: on },
    },
  }));

  return <div>
    {/* SMTP status banner — the toggles below only matter if SMTP is live */}
    <Cd style={{ marginBottom: 14, padding: 14 }}>
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-[6px] flex-shrink-0`} style={{ background: cfg.smtp.configured ? K.g : K.r }} />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-t0">
            {cfg.smtp.configured ? 'SMTP connected' : 'SMTP not configured'}
          </div>
          {cfg.smtp.configured
            ? <div className="text-[14px] text-t3 mt-[2px]">
                Host: <span className="font-mono">{cfg.smtp.host}</span> · From: <span className="font-mono">{cfg.smtp.from}</span>
                {cfg.smtp.hr_cc && <> · HR CC: <span className="font-mono">{cfg.smtp.hr_cc}</span></>}
              </div>
            : <div className="text-[14px] text-r mt-[2px]">
                Set <span className="font-mono">SMTP_HOST</span>, <span className="font-mono">SMTP_USER</span>, <span className="font-mono">SMTP_PASS</span>, and <span className="font-mono">MAIL_FROM</span> in <span className="font-mono">server/.env</span> then restart the backend. Zoho requires an App Password (not the account password).
              </div>}
        </div>
      </div>
    </Cd>

    {/* Global kill-switch */}
    <Cd style={{ marginBottom: 14 }}>
      <div className="text-[14px] font-bold text-t0 mb-1">Master Switch</div>
      <div className="text-[14px] text-t3 mb-2">One click to silence every automated email the system might dispatch. Individual toggles below are ignored when this is off.</div>
      <Toggle
        checked={cfg.enabled}
        onChange={v => setCfg({ ...cfg, enabled: v })}
        label={cfg.enabled ? 'Outbound email: ON' : 'Outbound email: OFF (all mail suppressed)'}
        hint="When off, generated PDFs still file to the employee's Documents tab; only the email dispatch is skipped."
      />
    </Cd>

    {/* Exit-document category + per-type */}
    <Cd style={{ marginBottom: 14 }}>
      <div className="text-[14px] font-bold text-t0 mb-1">Exit Documents</div>
      <div className="text-[14px] text-t3 mb-3">Currently the only email category wired end-to-end. More categories (approvals, leaves) can be added later without touching this tab.</div>
      <Toggle
        checked={cfg.exit_docs.enabled}
        disabled={!cfg.enabled}
        onChange={v => setCfg({ ...cfg, exit_docs: { ...cfg.exit_docs, enabled: v } })}
        label={cfg.exit_docs.enabled ? 'Exit document emails: ON' : 'Exit document emails: OFF'}
        hint="Master switch for all 5 exit-doc letters below."
      />
      <div className="mt-3 pl-6 border-l-2 border-b0">
        {DOC_TYPES.map(([docType, hint]) => (
          <Toggle
            key={docType}
            checked={cfg.exit_docs.per_type[docType] !== false}
            disabled={!cfg.enabled || !cfg.exit_docs.enabled}
            onChange={v => setPerType(docType, v)}
            label={docType}
            hint={hint}
          />
        ))}
      </div>
    </Cd>

    <div className="flex items-center gap-3">
      <Bt icon={CheckCircle} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Email Settings'}</Bt>
      {msg && <div className={`text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
    </div>
  </div>;
};

const RecruitmentOptionsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  React.useEffect(() => {
    api.get('/api/recruitment/config').then(setCfg).catch(() => setCfg({}));
  }, []);
  if (!cfg) return null;

  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3000); };

  const setList = (key, list) => setCfg(prev => ({ ...prev, [key]: list }));

  const save = async () => {
    setSaving(true);
    try {
      const upd = await api.put('/api/recruitment/config', {
        sources: cfg.sources || [],
        notice_periods: cfg.notice_periods || [],
        employee_types: cfg.employee_types || [],
        qualifications: cfg.qualifications || [],
      });
      setCfg(upd);
      flash('Saved');
      notify('Recruitment options saved', { title: 'Saved', variant: 'success' });
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to save recruitment options', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const Section = ({ label, hint, k, placeholder }) => (
    <div className="mb-3">
      <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-1">{label}</div>
      {hint && <div className="text-[12px] text-t3 mb-2">{hint}</div>}
      <DeductionTypeChips
        types={cfg[k] || []}
        onChange={list => setList(k, list)}
        placeholder={placeholder}
        addLabel="Add"
      />
    </div>
  );

  return (
    <Cd style={{ marginBottom: 14 }}>
      <div className="text-[14px] font-bold text-t0 mb-1">Recruitment Options</div>
      <div className="text-[14px] text-t3 mb-3">
        Dropdown lists behind the Add Candidate + Raise Hiring Request forms. Add channels, qualifications, or notice-period options your org uses. Existing candidate rows keep their value even if HR removes it from a list — only new form submissions are constrained to the current list.
      </div>
      <Section label="Candidate Sources"   hint='Job boards / referral channels HR sources from.' k="sources"          placeholder='e.g. Foundit' />
      <Section label="Notice Periods"      hint='Ordered dropdown values on the candidate form.'  k="notice_periods"   placeholder='e.g. 120 days' />
      <Section label="Employee Types"      hint='Extend if you engage worker classes beyond full / contract / freelancer.' k="employee_types" placeholder='e.g. Intern' />
      <Section label="Qualifications"      hint='Shared with the Raise Hiring Request "Minimum Qualification" dropdown.' k="qualifications" placeholder='e.g. B.Arch' />
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-b0">
        <Bt icon={CheckCircle} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Options'}</Bt>
        {msg && <div className={`text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
      </div>
    </Cd>
  );
};

// Vendor tax dropdowns — Accounts can extend the vendor categories and
// TDS section list. Mirror of RecruitmentOptionsCard, kept next to it
// because Payroll settings feels like the natural home for "editable
// enums used across finance forms".
const VendorTaxOptionsCard = () => {
  const [cats, setCats] = useState(null);
  const [tdsSections, setTdsSections] = useState(null);
  const [msg, setMsg] = useState('');
  const [newCat, setNewCat] = useState({ code: '', name: '', sec: '', rate: '0' });
  const [savingCat, setSavingCat] = useState(false);
  const [savingTds, setSavingTds] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // code of vendor category row being patched/deleted

  const load = () => {
    api.get('/api/vendors/categories').then(setCats).catch(() => setCats([]));
    api.get('/api/vendors/config').then(cfg => setTdsSections(cfg.tds_sections || [])).catch(() => setTdsSections([]));
  };
  React.useEffect(load, []);

  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3000); };

  const addCat = async () => {
    const code = newCat.code.trim().toUpperCase();
    const name = newCat.name.trim();
    if (!code || !name) { flash('Code and name are required', true); return; }
    setSavingCat(true);
    try {
      await api.post('/api/vendors/categories', {
        code, name,
        default_tds_section: newCat.sec.trim() || null,
        default_tds_rate: parseFloat(newCat.rate) || 0,
      });
      setNewCat({ code: '', name: '', sec: '', rate: '0' });
      flash('Category added');
      notify('Vendor category added', { title: 'Added', variant: 'success' });
      load();
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to add vendor category', { title: 'Error', variant: 'error' }); }
    finally { setSavingCat(false); }
  };

  const patchCat = async (code, patch) => {
    setRowBusy(code);
    try {
      await api.put(`/api/vendors/categories/${code}`, patch);
      flash('Saved');
      notify('Vendor category saved', { title: 'Saved', variant: 'success' });
      load();
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to save vendor category', { title: 'Error', variant: 'error' }); }
    finally { setRowBusy(null); }
  };

  const removeCat = async (code, name) => {
    if (!window.confirm(`Delete vendor category "${name}"?\n\nIf any bills use it, the category will be deactivated (existing bills keep the label) instead of hard-deleted.`)) return;
    setRowBusy(code);
    try {
      const r = await api.del(`/api/vendors/categories/${code}`);
      const msg = r.deactivated ? `Deactivated — ${r.linked_bills} bill(s) still reference it` : 'Category deleted';
      flash(msg);
      notify(msg, { title: r.deactivated ? 'Deactivated' : 'Deleted', variant: 'success' });
      load();
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to delete vendor category', { title: 'Error', variant: 'error' }); }
    finally { setRowBusy(null); }
  };

  const saveTds = async () => {
    setSavingTds(true);
    try {
      const cfg = await api.put('/api/vendors/config', { tds_sections: tdsSections });
      setTdsSections(cfg.tds_sections || []);
      flash('TDS sections saved');
      notify('TDS sections saved', { title: 'Saved', variant: 'success' });
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to save TDS sections', { title: 'Error', variant: 'error' }); }
    finally { setSavingTds(false); }
  };

  const addTds = () => setTdsSections([...(tdsSections || []), { code: '', label: '' }]);
  const patchTds = (i, patch) => setTdsSections(tdsSections.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeTds = (i) => setTdsSections(tdsSections.filter((_, idx) => idx !== i));

  if (cats === null || tdsSections === null) return null;

  const inputCls = "px-2 py-[6px] bg-bg2 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none";

  return (
    <Cd style={{ marginBottom: 14 }}>
      <div className="text-[14px] font-bold text-t0 mb-1">Vendor Tax Options</div>
      <div className="text-[14px] text-t3 mb-3">
        Dropdown lists behind the Vendor form's Tax Defaults section. Add categories your org actually uses (e.g. "Marketing", "Housekeeping") and any TDS sections you deduct against. Existing vendor / bill records keep their value even if Accounts removes an option here — only new picks are constrained to the current list.
      </div>

      {/* Categories */}
      <div className="mb-4">
        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-2">Vendor Categories</div>
        <div className="rounded-lg border border-b0 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2">
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Code</th>
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Name</th>
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Default TDS</th>
              <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">Rate %</th>
              <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Active</th>
              <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
            </tr></thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.cd} className="border-b border-b0">
                  <td className="px-2 py-[6px] font-mono text-t2 text-[14px]">{c.cd}</td>
                  <td className="px-2 py-[6px]">
                    <input
                      className={inputCls + ' w-full'}
                      defaultValue={c.nm}
                      onBlur={e => e.target.value !== c.nm && patchCat(c.cd, { name: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-[6px]">
                    <select
                      className={inputCls}
                      value={c.sec || ''}
                      onChange={e => patchCat(c.cd, { default_tds_section: e.target.value })}
                    >
                      <option value="">—</option>
                      {tdsSections.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-[6px] text-right">
                    <input
                      className={inputCls + ' w-16 text-right'}
                      type="number" step="0.01" min="0"
                      defaultValue={c.rate}
                      onBlur={e => Number(e.target.value) !== c.rate && patchCat(c.cd, { default_tds_rate: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-[6px] text-center">
                    <input type="checkbox" checked={c.active} onChange={e => patchCat(c.cd, { is_active: e.target.checked })} />
                  </td>
                  <td className="px-2 py-[6px] text-right">
                    <button
                      onClick={() => removeCat(c.cd, c.nm)}
                      disabled={rowBusy === c.cd}
                      className="text-r text-[14px] font-semibold bg-transparent border-0 cursor-pointer disabled:opacity-50"
                    >{rowBusy === c.cd ? 'Deleting…' : 'Delete'}</button>
                  </td>
                </tr>
              ))}
              {/* Add row */}
              <tr className="bg-bg2">
                <td className="px-2 py-[6px]">
                  <input className={inputCls + ' w-20 uppercase'} placeholder="CODE" value={newCat.code} onChange={e => setNewCat({ ...newCat, code: e.target.value.toUpperCase() })} maxLength={15} />
                </td>
                <td className="px-2 py-[6px]">
                  <input className={inputCls + ' w-full'} placeholder="Category name" value={newCat.name} onChange={e => setNewCat({ ...newCat, name: e.target.value })} />
                </td>
                <td className="px-2 py-[6px]">
                  <select className={inputCls} value={newCat.sec} onChange={e => setNewCat({ ...newCat, sec: e.target.value })}>
                    <option value="">—</option>
                    {tdsSections.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                  </select>
                </td>
                <td className="px-2 py-[6px] text-right">
                  <input className={inputCls + ' w-16 text-right'} type="number" step="0.01" min="0" value={newCat.rate} onChange={e => setNewCat({ ...newCat, rate: e.target.value })} />
                </td>
                <td className="px-2 py-[6px] text-center text-t3">—</td>
                <td className="px-2 py-[6px] text-right">
                  <Bt s="xs" icon={Plus} onClick={addCat} disabled={savingCat}>{savingCat ? 'Adding…' : 'Add'}</Bt>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
        <div className="text-[12px] text-t3 mt-1">Category names / TDS / rate / active auto-save on change. Delete falls back to Deactivate when bills reference the category.</div>
      </div>

      {/* TDS sections */}
      <div className="mb-3">
        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-2">TDS Sections</div>
        <div className="text-[12px] text-t3 mb-2">Sections available in the Vendor form's "Default TDS Section" picker. Code is the section number (e.g. 194J); label is what HR sees.</div>
        <div className="rounded-lg border border-b0 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2">
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Code</th>
              <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Label</th>
              <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
            </tr></thead>
            <tbody>
              {tdsSections.map((s, i) => (
                <tr key={i} className="border-b border-b0">
                  <td className="px-2 py-[6px]">
                    <input className={inputCls + ' w-20'} value={s.code} onChange={e => patchTds(i, { code: e.target.value })} placeholder="194J" />
                  </td>
                  <td className="px-2 py-[6px]">
                    <input className={inputCls + ' w-full'} value={s.label || ''} onChange={e => patchTds(i, { label: e.target.value })} placeholder="194J — Professional" />
                  </td>
                  <td className="px-2 py-[6px] text-right">
                    <button onClick={() => removeTds(i)} className="text-r text-[14px] font-semibold bg-transparent border-0 cursor-pointer">Remove</button>
                  </td>
                </tr>
              ))}
              {tdsSections.length === 0 && (
                <tr><td colSpan={3} className="text-center px-3 py-3 text-t3 text-[14px] italic">No TDS sections — add one below.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Bt s="sm" v="secondary" icon={Plus} onClick={addTds}>Add TDS Section</Bt>
          <Bt s="sm" v="success" icon={CheckCircle} onClick={saveTds} disabled={savingTds}>{savingTds ? 'Saving…' : 'Save TDS List'}</Bt>
        </div>
      </div>

      {msg && <div className={`text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
    </Cd>
  );
};

const PayrollSettingsTab = ({ companies }) => {
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  React.useEffect(() => {
    if (!companyId && companies.length > 0) setCompanyId(String(companies[0].id));
  }, [companies, companyId]);

  const reload = (cid) => {
    if (!cid) return;
    api.get(`/api/payroll/company-settings/${cid}`)
       .then(d => {
         setData(d);
         const ov = d.override || {};
         const ss = ov.salary_structure || {};
         setVals({
           // payroll cycle
           pay_day:               ov.pay_day ?? '',
           cutoff_day:            ov.cutoff_day ?? '',
           overtime_rate:         ov.overtime_rate ?? '',
           working_days_mode:     ov.working_days_mode ?? '',
           min_working_days:      ov.min_working_days ?? '',
           // structure (overrides only)
           basic_pct:             ss.basic_pct ?? '',
           hra_pct:               ss.hra_pct ?? '',
           special_pct:           ss.special_pct ?? '',
           conveyance:            ss.conveyance ?? '',
           medical:               ss.medical ?? '',
           pf_rate:               ss.pf_rate ?? '',
           pf_ceil:               ss.pf_ceil ?? '',
           pt_rate:               ss.pt_rate ?? '',
           // statutory compliance — '' = inherit, 'true'/'false' = override
           pf_enabled:            ss.pf_enabled       === undefined ? '' : String(ss.pf_enabled),
           esi_enabled:           ss.esi_enabled      === undefined ? '' : String(ss.esi_enabled),
           pt_enabled:            ss.pt_enabled       === undefined ? '' : String(ss.pt_enabled),
           gratuity_enabled:      ss.gratuity_enabled === undefined ? '' : String(ss.gratuity_enabled),
           gratuity_rate:         ss.gratuity_rate ?? '',
           // tds
           tds_mode:              ov.tds_mode ?? '',
           tds_default_rate:      ov.tds_default_rate ?? '',
           // tds_slabs: undefined means "inherit from global"; an array
           // (even []) means "override". Don't fall back to '' because
           // that would mask a real override of [].
           tds_slabs:             Array.isArray(ov.tds_slabs) ? ov.tds_slabs : undefined,
           // bank
           default_bank_format:   d.company?.default_bank_format ?? 'generic',
         });
       })
       .catch(e => setMsg('Error: ' + (e.message || 'Load failed')));
  };
  React.useEffect(() => { reload(companyId); }, [companyId]);

  const flash = (m, isErr = false) => { setMsg((isErr ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3500); };

  const save = async () => {
    setSaving(true);
    try {
      // Build the override JSONB from non-empty values.
      const num = v => (v === '' || v === null) ? null : Number(v);
      const ss = {};
      if (vals.basic_pct   !== '') ss.basic_pct   = num(vals.basic_pct);
      if (vals.hra_pct     !== '') ss.hra_pct     = num(vals.hra_pct);
      if (vals.special_pct !== '') ss.special_pct = num(vals.special_pct);
      if (vals.conveyance  !== '') ss.conveyance  = num(vals.conveyance);
      if (vals.medical     !== '') ss.medical     = num(vals.medical);
      if (vals.pf_rate     !== '') ss.pf_rate     = num(vals.pf_rate);
      if (vals.pf_ceil     !== '') ss.pf_ceil     = num(vals.pf_ceil);
      if (vals.pt_rate     !== '') ss.pt_rate     = num(vals.pt_rate);
      // Statutory toggles — tri-state. '' = inherit (skip), 'true' /
      // 'false' = explicit per-company override.
      if (vals.pf_enabled       === 'true')  ss.pf_enabled       = true;
      else if (vals.pf_enabled  === 'false') ss.pf_enabled       = false;
      if (vals.esi_enabled      === 'true')  ss.esi_enabled      = true;
      else if (vals.esi_enabled === 'false') ss.esi_enabled      = false;
      if (vals.pt_enabled       === 'true')  ss.pt_enabled       = true;
      else if (vals.pt_enabled  === 'false') ss.pt_enabled       = false;
      if (vals.gratuity_enabled      === 'true')  ss.gratuity_enabled = true;
      else if (vals.gratuity_enabled === 'false') ss.gratuity_enabled = false;
      if (vals.gratuity_rate !== '') ss.gratuity_rate = num(vals.gratuity_rate);

      const body = {};
      if (vals.pay_day            !== '') body.pay_day            = num(vals.pay_day);
      if (vals.cutoff_day         !== '') body.cutoff_day         = num(vals.cutoff_day);
      if (vals.overtime_rate      !== '') body.overtime_rate      = num(vals.overtime_rate);
      if (vals.working_days_mode  !== '') body.working_days_mode  = vals.working_days_mode;
      if (vals.min_working_days   !== '') body.min_working_days   = num(vals.min_working_days);
      if (Object.keys(ss).length)         body.salary_structure   = ss;
      if (vals.tds_mode           !== '') body.tds_mode           = vals.tds_mode;
      if (vals.tds_default_rate   !== '') body.tds_default_rate   = num(vals.tds_default_rate);
      if (Array.isArray(vals.tds_slabs)) body.tds_slabs = vals.tds_slabs;
      if (vals.default_bank_format)       body.default_bank_format = vals.default_bank_format;

      await api.put(`/api/payroll/company-settings/${companyId}`, body);
      flash('Saved');
      notify('Company payroll settings saved', { title: 'Saved', variant: 'success' });
      reload(companyId);
    } catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save payroll settings', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  const inheritedNote = (key, group) => {
    const has = group ? data?.override?.[group]?.[key] !== undefined : data?.override?.[key] !== undefined;
    if (has) return <span className="text-[12px] text-pu uppercase ml-1">overridden</span>;
    const eff = group ? data?.effective?.[group]?.[key] : data?.effective?.[key];
    return <span className="text-[12px] text-t3 italic ml-1">inherited: {eff != null ? String(eff) : '—'}</span>;
  };

  return <div>
    <DeductionRulesCard />
    {msg && <div className={`px-3 py-2 mb-[10px] rounded-lg text-[14px] font-semibold ${msg.startsWith('Error') ? 'bg-rb text-r border border-rd' : 'bg-gb text-g border border-gd'}`}>{msg}</div>}
    <Cd style={{ marginBottom: 12 }}>
      <div className="flex gap-3 items-end flex-wrap">
        <div className="min-w-[240px]">
          <div className={lbl}>Company</div>
          <Sl options={companies.map(c => ({ v: String(c.id), l: c.name }))} value={companyId} onChange={setCompanyId} />
        </div>
        <div className="text-[14px] text-t3 self-center max-w-[400px]">
          Edit per-company overrides. Leave a field blank to inherit the org-wide default.
          {data?.updated_at && <> · Last edit: {new Date(data.updated_at).toLocaleString('en-IN')}</>}
        </div>
      </div>
    </Cd>

    {data && <>
      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={Wallet} title="Payroll Cycle (overrides)" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className={lbl}>Pay Day {inheritedNote('pay_day')}</div>
            <Inp type="number" placeholder="1–31" value={String(vals.pay_day)} onChange={v => setVals(p => ({ ...p, pay_day: v }))} />
          </div>
          <div>
            <div className={lbl}>Cutoff Day {inheritedNote('cutoff_day')}</div>
            <Inp type="number" placeholder="1–31" value={String(vals.cutoff_day)} onChange={v => setVals(p => ({ ...p, cutoff_day: v }))} />
          </div>
          <div>
            <div className={lbl}>Overtime Rate (×) {inheritedNote('overtime_rate')}</div>
            <Inp type="number" placeholder="1.5" value={String(vals.overtime_rate)} onChange={v => setVals(p => ({ ...p, overtime_rate: v }))} />
          </div>
          <div>
            <div className={lbl}>Working Days Mode {inheritedNote('working_days_mode')}</div>
            <Sl options={[
              { v: '', l: '— inherit —' },
              { v: 'fixed', l: 'Fixed (use min_working_days)' },
              { v: 'calendar', l: 'Calendar (full month)' },
              { v: 'calendar_minus_weekends', l: 'Calendar minus weekends' },
              { v: 'calendar_minus_weekends_holidays', l: 'Calendar minus weekends + holidays' },
            ]} value={vals.working_days_mode} onChange={v => setVals(p => ({ ...p, working_days_mode: v }))} />
          </div>
          <div>
            <div className={lbl}>Min Working Days {inheritedNote('min_working_days')}</div>
            <Inp type="number" placeholder="22" value={String(vals.min_working_days)} onChange={v => setVals(p => ({ ...p, min_working_days: v }))} />
          </div>
        </div>
      </Cd>

      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={DollarSign} title="Statutory Compliance (overrides)" />
        <div className="text-[14px] text-t3 mb-3 -mt-1">
          Toggle which statutory deductions apply at this company. Leave a field on <i>Inherit</i> to follow the org-wide default; override to force on or off.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div>
            <div className={lbl}>Provident Fund (PF) {inheritedNote('pf_enabled', 'salary_structure')}</div>
            <Sl options={[
              { v: '', l: '— inherit —' },
              { v: 'true', l: 'Enabled' },
              { v: 'false', l: 'Disabled' },
            ]} value={vals.pf_enabled} onChange={v => setVals(p => ({ ...p, pf_enabled: v }))} />
          </div>
          <div>
            <div className={lbl}>ESI {inheritedNote('esi_enabled', 'salary_structure')}</div>
            <Sl options={[
              { v: '', l: '— inherit —' },
              { v: 'true', l: 'Enabled' },
              { v: 'false', l: 'Disabled' },
            ]} value={vals.esi_enabled} onChange={v => setVals(p => ({ ...p, esi_enabled: v }))} />
          </div>
          <div>
            <div className={lbl}>Professional Tax {inheritedNote('pt_enabled', 'salary_structure')}</div>
            <Sl options={[
              { v: '', l: '— inherit —' },
              { v: 'true', l: 'Enabled' },
              { v: 'false', l: 'Disabled' },
            ]} value={vals.pt_enabled} onChange={v => setVals(p => ({ ...p, pt_enabled: v }))} />
          </div>
          <div>
            <div className={lbl}>Gratuity {inheritedNote('gratuity_enabled', 'salary_structure')}</div>
            <Sl options={[
              { v: '', l: '— inherit —' },
              { v: 'true', l: 'Enabled' },
              { v: 'false', l: 'Disabled' },
            ]} value={vals.gratuity_enabled} onChange={v => setVals(p => ({ ...p, gratuity_enabled: v }))} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <div className={lbl}>PT Amount (₹/mo) {inheritedNote('pt_rate', 'salary_structure')}</div>
            <Inp type="number" placeholder="200" value={String(vals.pt_rate)} onChange={v => setVals(p => ({ ...p, pt_rate: v }))} />
          </div>
          <div>
            <div className={lbl}>Gratuity Rate % {inheritedNote('gratuity_rate', 'salary_structure')}</div>
            <Inp type="number" placeholder="4.81" value={String(vals.gratuity_rate)} onChange={v => setVals(p => ({ ...p, gratuity_rate: v }))} />
          </div>
        </div>
      </Cd>

      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={DollarSign} title="TDS (overrides)" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <div className={lbl}>TDS Mode {inheritedNote('tds_mode')}</div>
            <Sl options={[
              { v: '', l: '— inherit —' },
              { v: 'flat', l: 'Flat % on monthly salary' },
              { v: 'slab', l: 'Progressive slab (annual)' },
              { v: 'manual_per_employee', l: 'Per-employee override' },
            ]} value={vals.tds_mode} onChange={v => setVals(p => ({ ...p, tds_mode: v }))} />
          </div>
          <div>
            <div className={lbl}>TDS Default Rate % {inheritedNote('tds_default_rate')}</div>
            <Inp type="number" placeholder="8" value={String(vals.tds_default_rate)} onChange={v => setVals(p => ({ ...p, tds_default_rate: v }))} />
          </div>
        </div>

        {/* Slab editor — only meaningful when mode='slab'. Shows the
            effective slabs (override or inherited), lets HR add/edit/
            remove rows. Last slab usually has up_to_annual = blank
            ("everything above"). Saved as part of the override JSONB on
            this same Save button below. */}
        <TdsSlabEditor effectiveMode={vals.tds_mode || data?.effective?.tds_mode || 'flat'}
                       slabs={vals.tds_slabs ?? data?.effective?.tds_slabs ?? []}
                       inherited={vals.tds_slabs == null}
                       onChange={(s) => setVals(p => ({ ...p, tds_slabs: s }))}
                       onRevert={() => setVals(p => { const n = { ...p }; delete n.tds_slabs; return n; })} />
      </Cd>

      <Cd style={{ marginBottom: 12 }}>
        <Sec icon={Building} title="Bank File Format" />
        <div className="text-[14px] text-t3 mb-2">Default template the Bank File tab uses for this company's NEFT batch download.</div>
        <div className="max-w-[300px]">
          <Sl options={[
            { v: 'generic', l: 'Generic CSV' },
            { v: 'hdfc',    l: 'HDFC NEFT' },
            { v: 'icici',   l: 'ICICI Corporate' },
            { v: 'axis',    l: 'Axis CMS' },
            { v: 'sbi',     l: 'SBI Corporate' },
          ]} value={vals.default_bank_format} onChange={v => setVals(p => ({ ...p, default_bank_format: v }))} />
        </div>
      </Cd>

      <div className="flex gap-2">
        <Bt icon={Check} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Company Settings'}</Bt>
        <span className="text-[14px] text-t3 self-center">Saving overwrites this company's overrides only — global defaults are untouched.</span>
      </div>
    </>}
  </div>;
};

// Add/edit an expense category. Receipt rule: always (0) / above ₹N / never (null).
const ExpenseCategoryModal = ({ cat, onClose, onSaved }) => {
  const isNew = !cat;
  const initMode = isNew ? 'above' : (cat.rcptAbove === null ? 'never' : cat.rcptAbove === 0 ? 'always' : 'above');
  const [f, setF] = useState({
    name: cat?.nm || "",
    max_amount: cat ? String(cat.mx || 0) : "0",
    receipt_mode: initMode,
    receipt_above: (cat && cat.rcptAbove > 0) ? String(cat.rcptAbove) : "500",
    color: cat?.clr || "#5F6CF0",
  });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim()) return setErr("Name is required");
    setSaving(true); setErr("");
    const body = { name: f.name.trim(), max_amount: Number(f.max_amount) || 0, receipt_mode: f.receipt_mode, receipt_above: Number(f.receipt_above) || 0, color: f.color };
    try {
      if (isNew) await api.post('/api/expenses/categories', body);
      else await api.put('/api/expenses/categories/' + cat.cd, body);
      notify(isNew ? "Expense category added" : "Expense category updated", { title: isNew ? "Added" : "Saved", variant: "success" });
      onSaved();
    } catch (e) { setErr(e.message || "Save failed"); notify(e.message || "Failed to save expense category", { title: "Error", variant: "error" }); } finally { setSaving(false); }
  };
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[628px]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">{isNew ? "Add Category" : "Edit Category"}</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
      <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Name <span className="text-r">*</span></div><Inp value={f.name} onChange={v => setF({ ...f, name: v })} placeholder="e.g. Fuel" /></div>
      <div className="grid grid-cols-2 gap-[10px] mb-3">
        <div><div className="text-[14px] text-t2 mb-[3px]">Max amount (0 = no limit)</div><Inp type="number" value={f.max_amount} onChange={v => setF({ ...f, max_amount: v })} /></div>
        <div><div className="text-[14px] text-t2 mb-[3px]">Colour</div><input type="color" value={f.color} onChange={e => setF({ ...f, color: e.target.value })} className="w-full h-[34px] bg-bg2 border border-b0 rounded-[10px] cursor-pointer p-1" /></div>
      </div>
      <div className="mb-3">
        <div className="text-[14px] text-t2 mb-[3px]">Receipt required</div>
        <Sl options={[{ v: "always", l: "Always required" }, { v: "above", l: "Required above an amount" }, { v: "never", l: "Not required" }]} value={f.receipt_mode} onChange={v => setF({ ...f, receipt_mode: v })} />
      </div>
      {f.receipt_mode === 'above' && <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Require receipt above (₹)</div><Inp type="number" value={f.receipt_above} onChange={v => setF({ ...f, receipt_above: v })} /></div>}
      {err && <div className="text-[14px] text-r mb-2">{err}</div>}
      <div className="flex gap-[6px]"><Bt full onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Bt><Bt v="secondary" full onClick={onClose}>Cancel</Bt></div>
    </div>
  </div>;
};

// Expense settings: manage categories (add/edit/deactivate/delete) and the
// per-location Centre Head approval limit. Also hosts the Vendor Tax
// Options sub-tab (categories + TDS section lists) since those are all
// finance-side pickers and living here keeps them close to Accounts.
const ExpenseSettingsTab = () => {
  const [subTab, setSubTab] = useState('general'); // general | vendor-tax
  // ── Categories ──
  const [cats, setCats] = useState(null);
  const [catModal, setCatModal] = useState(null); // {} = new, cat = edit, null = closed
  const [catMsg, setCatMsg] = useState("");
  const [catBusy, setCatBusy] = useState(null);
  const loadCats = () => api.get('/api/expenses/categories?usage=1').then(d => setCats(d || [])).catch(e => setCatMsg('Error: ' + (e.message || 'load failed')));
  React.useEffect(() => { loadCats(); }, []);
  const flashCat = (m, e = false) => { setCatMsg((e ? 'Error: ' : '') + m); setTimeout(() => setCatMsg(''), 3000); };
  const rcptLabel = (c) => c.rcptAbove === null ? 'Not required' : c.rcptAbove === 0 ? 'Always' : `Above ₹${c.rcptAbove.toLocaleString('en-IN')}`;
  const delCat = async (c) => {
    const linked = c.linked || 0;
    const msg = linked > 0
      ? `"${c.nm}" has ${linked} linked expense(s) and can't be deleted. Deactivate it so no new claims can use it?`
      : `Delete category "${c.nm}"? This cannot be undone.`;
    if (!(await confirmDialog(msg))) return;
    setCatBusy(c.cd);
    try {
      const r = await api.del('/api/expenses/categories/' + c.cd);
      const done = r.deleted ? 'Category deleted' : `Deactivated (${r.linked} linked claim${r.linked === 1 ? '' : 's'})`;
      flashCat(done);
      notify(done, { title: r.deleted ? 'Deleted' : 'Deactivated', variant: 'success' });
      loadCats();
    }
    catch (e) { flashCat(e.message || 'Failed', true); notify(e.message || 'Failed to delete expense category', { title: 'Error', variant: 'error' }); }
    finally { setCatBusy(null); }
  };
  const toggleActive = async (c) => {
    setCatBusy(c.cd);
    try {
      await api.put('/api/expenses/categories/' + c.cd, { is_active: !c.active });
      notify(c.active ? 'Category deactivated' : 'Category activated', { title: 'Saved', variant: 'success' });
      loadCats();
    }
    catch (e) { flashCat(e.message || 'Failed', true); notify(e.message || 'Failed to update expense category', { title: 'Error', variant: 'error' }); }
    finally { setCatBusy(null); }
  };

  // ── Approval limits (per office) ──
  const [rows, setRows] = useState(null);
  const [vals, setVals] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState("");
  React.useEffect(() => {
    api.get('/api/expenses/office-limits')
      .then(d => { setRows(d || []); const v = {}; (d || []).forEach(o => { v[o.id] = String(o.limit ?? 0); }); setVals(v); })
      .catch(e => setMsg('Error: ' + (e.message || 'Load failed')));
  }, []);
  const flash = (m, isErr = false) => { setMsg((isErr ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3000); };
  const saveLimit = async (id) => {
    setSavingId(id);
    try { await api.put('/api/expenses/office-limits/' + id, { limit: Number(vals[id] || 0) }); flash('Saved'); notify('Approval limit saved', { title: 'Saved', variant: 'success' }); }
    catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save approval limit', { title: 'Error', variant: 'error' }); }
    finally { setSavingId(null); }
  };

  const subTabCls = (k) => `px-3 py-[6px] bg-transparent border-0 text-[14px] cursor-pointer ${subTab === k ? 'border-b-2 border-p text-p font-semibold' : 'border-b-2 border-transparent text-t2 font-medium'}`;

  return <div>
    <div className="flex gap-0 mb-4 border-b border-b0 flex-wrap whitespace-nowrap">
      <button className={subTabCls('general')} onClick={() => setSubTab('general')}>General</button>
      <button className={subTabCls('vendor-tax')} onClick={() => setSubTab('vendor-tax')}>Vendor Tax Options</button>
    </div>

    {subTab === 'vendor-tax' && <VendorTaxOptionsCard />}

    {subTab === 'general' && <div className="grid gap-6">
    {/* Categories */}
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[15px] font-bold text-t0">Expense Categories</div>
        <Bt s="sm" icon={Plus} onClick={() => setCatModal({})}>Add Category</Bt>
      </div>
      <div className="text-[14px] text-t2 mb-2 max-w-[680px]">Define categories, their spending cap, and when a receipt is required. A category with linked claims can't be deleted — it's deactivated instead so it can't be chosen for new claims (existing claims stay intact).</div>
      {!cats ? <div className="text-t3 text-[14px]">Loading…</div> : <div className="grid gap-2 max-w-[760px]">
        {cats.map(c => <Cd key={c.cd} style={{ padding: 12 }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: c.clr }} />
              <div className="min-w-0">
                <div className="text-[14px] font-bold text-t0 flex items-center gap-2">{c.nm} {!c.active && <Bg c={K.t3} sm>Inactive</Bg>}</div>
                <div className="text-[12px] text-t3">Max: {c.mx > 0 ? `₹${c.mx.toLocaleString('en-IN')}` : 'No limit'} · Receipt: {rcptLabel(c)} · {c.linked || 0} claim(s)</div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Bt v="ghost" s="xs" icon={Edit} onClick={() => setCatModal(c)}>Edit</Bt>
              <Bt v="ghost" s="xs" disabled={catBusy === c.cd} onClick={() => toggleActive(c)}>{catBusy === c.cd ? 'Saving…' : (c.active ? 'Deactivate' : 'Activate')}</Bt>
              <Bt v="ghost" s="xs" icon={Trash2} disabled={catBusy === c.cd} onClick={() => delCat(c)} />
            </div>
          </div>
        </Cd>)}
        {cats.length === 0 && <div className="text-t3 text-[14px]">No categories yet — add one.</div>}
      </div>}
      {catMsg && <div className={`mt-2 text-[14px] font-semibold ${catMsg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{catMsg}</div>}
    </div>

    {/* Approval limits */}
    <div>
      <div className="text-[15px] font-bold text-t0 mb-2">Centre Head Approval Limits (per location)</div>
      <div className="text-[14px] text-t2 mb-3 max-w-[680px]">Maximum expense a <b className="text-t0">Centre Head</b> can approve for each location. Claims above the limit must be approved by an <b className="text-t0">Admin</b>. Set <b className="text-t0">0</b> to route every claim from that location to Admin.</div>
      {!rows ? <div className="text-t3 text-[14px]">Loading…</div> : <div className="grid gap-2 max-w-[640px]">
        {rows.map(o => <Cd key={o.id} style={{ padding: 12 }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div><div className="text-[14px] font-bold text-t0">{o.name}</div><div className="text-[12px] text-t3">{o.city || '—'}</div></div>
            <div className="flex items-center gap-2">
              <span className="text-[14px] text-t3 font-semibold">₹</span>
              <Inp type="number" value={vals[o.id] ?? ''} onChange={v => setVals(p => ({ ...p, [o.id]: v }))} style={{ width: 150 }} />
              <Bt s="sm" onClick={() => saveLimit(o.id)} disabled={savingId === o.id}>{savingId === o.id ? 'Saving…' : 'Save'}</Bt>
            </div>
          </div>
        </Cd>)}
        {rows.length === 0 && <div className="text-t3 text-[14px]">No offices configured.</div>}
      </div>}
      {msg && <div className={`mt-3 text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
    </div>

    {catModal !== null && <ExpenseCategoryModal cat={catModal.cd ? catModal : null} onClose={() => setCatModal(null)} onSaved={() => { setCatModal(null); loadCats(); }} />}
    </div>}
  </div>;
};

// Every Settings sub-tab, independently grantable via Settings > Permissions
// (module 'settings', one tab node per key here) — matches
// server/services/permissionCatalog.js's settings module exactly.
const SETTINGS_TABS = [
  { key: "general", label: "General" },
  { key: "leave_types", label: "Leave Types" },
  { key: "appraisal", label: "Appraisal" },
  { key: "compliance", label: "Compliance" },
  { key: "payroll", label: "Payroll" },
  { key: "salary", label: "Salary Structure" },
  { key: "expenses", label: "Expenses" },
  { key: "recruitment", label: "Recruitment" },
  { key: "email", label: "Email" },
  { key: "holidays", label: "Holidays" },
  { key: "offices", label: "Offices" },
  { key: "assets", label: "Assets" },
  { key: "separation", label: "Offboarding" },
  { key: "access", label: "Access Control" },
  { key: "permissions", label: "Permissions" },
  { key: "security", label: "Security" },
  { key: "deletion_log", label: "Deletion Log" },
];

export const SettingsPage = ({ settData, settLvTypes = [], settApCycles = [], cmpData, payData, holData = [], offices = [], companies = [], dashData, assetCats = [], assetTypeGroups = [], role, can, onRefresh, onLvTypesChange, onApCyclesChange, onHolDataChange, onOfficesChange, onCompaniesChange, onSettDataChange, onAssetCatsChange, onAssetTypeGroupsChange }) => {
  const visibleSettTabs = SETTINGS_TABS.filter(t => can('settings', t.key, null, 'view'));
  // Kept in the URL hash (#settings?stab=permissions) so Back/Forward step between Settings tabs instead of leaving the page.
  const [settTab, setSettTab] = useHashParam("stab", "general");
  // The hardcoded "general" default above may not be a tab this role can
  // actually see — land on the first one they can instead of a blank pane
  // with no active tab highlighted.
  useEffect(() => {
    if (visibleSettTabs.length && !visibleSettTabs.some(t => t.key === settTab)) {
      setSettTab(visibleSettTabs[0].key, { replace: true });
    }
  }, [visibleSettTabs, settTab]);
  const [deleteOfficeTarget, setDeleteOfficeTarget] = useState(null); // office object being confirmed for delete
  const [deleteOfficeConfirm, setDeleteOfficeConfirm] = useState("");
  const [deleteOfficeBusy, setDeleteOfficeBusy] = useState(false);
  const [lvTypeEdit, setLvTypeEdit] = useState(null);
  const [roleMatrix, setRoleMatrix] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [showForm, setShowForm] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");
  const [ltBusy, setLtBusy] = useState(null); // leave-type code being (de)activated
  const [cycBusy, setCycBusy] = useState(null); // appraisal cycle id being activated/closed
  const [roleBusy, setRoleBusy] = useState(null); // role id whose approval limits are saving
  const [companyBusy, setCompanyBusy] = useState(null); // company id being reactivated
  const [savingCompanyName, setSavingCompanyName] = useState(false);
  const [savingRegional, setSavingRegional] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const [ofcBusy, setOfcBusy] = useState(false); // holiday deletion in flight
  const [assetBusy, setAssetBusy] = useState(null); // asset group/type id:type being deleted
  const [permBusy, setPermBusy] = useState(null); // "role|mod|field" access-control toggle in flight
  // The `companies` prop from App.js is Active-only (feeds dropdowns
  // everywhere). Settings > Companies needs to show Inactive rows too,
  // so we keep a local `allCompanies` state fetched without the filter.
  // Any mutation (add/edit/deactivate/reactivate/purge) refreshes both.
  const [allCompanies, setAllCompanies] = useState([]);
  const reloadAllCompanies = () => api.get('/api/companies').then(d => setAllCompanies(Array.isArray(d) ? d : d.data || [])).catch(() => setAllCompanies([]));
  useEffect(() => { reloadAllCompanies(); }, []); // eslint-disable-line
  // Self-appraisal form questions (admin/HR configurable) — same
  // self-contained-fetch pattern as allCompanies above, since this list
  // only matters while the Appraisal tab is open.
  const [apQs, setApQs] = useState([]);
  const [apQBusy, setApQBusy] = useState(null);
  const reloadApQs = () => api.get('/api/appraisals/questions?all=true').then(d => setApQs(d.data || d || [])).catch(() => setApQs([]));
  useEffect(() => { reloadApQs(); }, []); // eslint-disable-line
  // Helper to refresh both the App-wide Active-only cache AND the local full list.
  const refreshCompaniesBoth = () => {
    api.get('/api/companies?active=true').then(d => onCompaniesChange && onCompaniesChange(Array.isArray(d) ? d : d.data || []));
    reloadAllCompanies();
  };

  const sd = settData || {}; const CD = cmpData || {};
  // Live roles from /api/roles — needed for the Appraisal Approval
  // Limits card because RL_DEFAULT (the offline fallback) doesn't
  // carry the `aa` (appraisal-approval-limit) field, which made the
  // card render "Appraisal: " with no value even though the backend
  // has real numbers per role.
  const [liveRoles, setLiveRoles] = React.useState(null);
  const reloadRoles = () => api.get('/api/roles').then(d => setLiveRoles(Array.isArray(d) ? d : d.data || [])).catch(() => setLiveRoles([]));
  React.useEffect(() => { reloadRoles(); }, []); // eslint-disable-line
  const roles = (liveRoles && liveRoles.length) ? liveRoles : (settData?.roles || RL_DEFAULT);
  const sLT = settLvTypes; const activeLT = sLT.filter(t => t.active !== false); const genderLT = sLT.filter(t => t.gen); const oneTimeLT = sLT.filter(t => t.accrual === "one_time");
  const reloadLT = () => api.get('/api/leaves/types').then(d => { const types = d.data || d || []; onLvTypesChange && onLvTypesChange(types); });
  const saveLvType = async (vals) => {
    const payload = { name: vals.name, default_balance: parseInt(vals.default_balance) || 0, is_unlimited: vals.is_unlimited === "true", requires_approval: vals.requires_approval !== "false", gender_specific: vals.gender_specific === "M" ? "M" : vals.gender_specific === "F" ? "F" : null, carry_forward: vals.carry_forward === "true", max_consecutive_days: parseInt(vals.max_consecutive_days) || 0, color: vals.color || "#5F6CF0", accrual: vals.accrual || "yearly", monthly_credit: parseFloat(vals.monthly_credit) || 0, paid: vals.paid !== "false", min_tenure_days: parseInt(vals.min_tenure_days) || 0, description: vals.description || "", return_required: vals.return_required === "true", document_required: vals.document_required === "true" };
    if (lvTypeEdit && !lvTypeEdit._new) { await api.put("/api/leaves/types/" + lvTypeEdit.cd, payload); } else { payload.code = vals.code; await api.post("/api/leaves/types", payload); }
    reloadLT();
  };
  const deactivateLT = async (code) => {
    setLtBusy(code);
    try {
      await api.del("/api/leaves/types/" + code); reloadLT();
      setSaveMsg("Leave type deactivated"); setTimeout(() => setSaveMsg(""), 3000);
      notify("Leave type deactivated", { title: "Saved", variant: "success" });
    } catch (e) {
      const msg = e.message || "Cannot deactivate — active leave requests exist for this type";
      setSaveMsg("Error: " + msg); setTimeout(() => setSaveMsg(""), 5000);
      notify(msg, { title: "Error", variant: "error" });
    } finally { setLtBusy(null); }
  };
  const stTabCls = (t) => `px-[18px] py-2 text-[14px] cursor-pointer transition-all duration-150 border-b-2 ${settTab === t ? "font-bold text-p bg-pg border-p" : "font-medium text-t2 bg-transparent border-transparent"}`;

  return <div>
    <h1 className="mt-0 mb-4 text-2xl font-extrabold text-t0">Settings</h1>
    <div className="flex gap-0 border-b border-b0 mb-4 flex-wrap whitespace-nowrap">
      {visibleSettTabs.map(t => (
        <button key={t.key} className={stTabCls(t.key)} onClick={() => setSettTab(t.key)}>
          {t.key === "leave_types" ? `Leave Types (${sLT.length})` : t.label}
        </button>
      ))}
    </div>
    {visibleSettTabs.length === 0 && (
      <div className="text-[14px] text-t3 py-8 text-center">You don't have access to any Settings sections. Ask an admin to grant you one from Settings → Permissions.</div>
    )}
    {settTab === "general" && (() => {
      const companyData = sd.config_data || sd || {};
      const saveCompany = async (vals) => {
        try {
          await api.put('/api/settings', { config_data: vals });
          api.get('/api/settings').then(d => onSettDataChange && onSettDataChange(d));
        } catch (e) { notify(e.message || 'Failed to save settings', { title: 'Error', variant: 'error' }); throw e; }
      };
      return <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          <Cd><Sec icon={Building} title="Organization Information" />
            <div className="mb-[10px]"><div className="text-[12px] text-t3 mb-[3px]">Organization Name</div><Inp value={editVals._compName || (companyData.company_name || "")} onChange={v => setEditVals(prev => ({ ...prev, _compName: v }))} placeholder="Your Organization Name" /></div>
            <div className="grid grid-cols-2 gap-[10px] mb-[10px]">
              <div><div className="text-[12px] text-t3 mb-[3px]">Total Employees</div><div className="px-[10px] py-[7px] bg-bg2 rounded-[10px] text-[15px] font-bold text-p border border-b0">{dashData?.total_employees || "—"}</div></div>
              <div><div className="text-[12px] text-t3 mb-[3px]">Offices / Locations</div><div className="px-[10px] py-[7px] bg-bg2 rounded-[10px] text-[15px] font-bold text-bl border border-b0">{dashData?.office_count || "—"}</div></div>
            </div>
            <Bt full disabled={savingCompanyName} onClick={async () => {
              setSavingCompanyName(true);
              try {
                await saveCompany({ ...companyData, company_name: editVals._compName || companyData.company_name });
                setEditVals({});
                notify('Organization information saved', { title: 'Saved', variant: 'success' });
              } catch (e) { /* already notified in saveCompany */ }
              finally { setSavingCompanyName(false); }
            }}>{savingCompanyName ? 'Saving…' : 'Save'}</Bt>
          </Cd>
          <Cd><Sec icon={CalendarDays} title="Regional Settings" />
            <div className="text-[14px] text-t3 mb-[10px]">These apply everywhere — attendance timestamps, payroll cycles, reports. A change takes effect immediately on next reload.</div>
            <div className="mb-[10px]">
              <div className="text-[12px] text-t3 mb-[3px]">Timezone</div>
              <Sl value={editVals._tz !== undefined ? editVals._tz : (companyData.timezone || "Asia/Kolkata")} onChange={v => setEditVals(prev => ({ ...prev, _tz: v }))} options={[
                { v: "Asia/Kolkata", l: "Asia/Kolkata — India Standard Time (IST)" },
                { v: "Asia/Dubai", l: "Asia/Dubai — Gulf Standard Time" },
                { v: "Asia/Singapore", l: "Asia/Singapore — SGT" },
                { v: "Asia/Hong_Kong", l: "Asia/Hong_Kong — HKT" },
                { v: "Asia/Tokyo", l: "Asia/Tokyo — JST" },
                { v: "Europe/London", l: "Europe/London — GMT/BST" },
                { v: "Europe/Berlin", l: "Europe/Berlin — CET/CEST" },
                { v: "America/New_York", l: "America/New_York — EST/EDT" },
                { v: "America/Chicago", l: "America/Chicago — CST/CDT" },
                { v: "America/Denver", l: "America/Denver — MST/MDT" },
                { v: "America/Los_Angeles", l: "America/Los_Angeles — PST/PDT" },
                { v: "Australia/Sydney", l: "Australia/Sydney — AEST/AEDT" },
                { v: "UTC", l: "UTC — Coordinated Universal Time" },
              ]} />
            </div>
            <div className="grid grid-cols-2 gap-[10px] mb-[10px]">
              <div>
                <div className="text-[12px] text-t3 mb-[3px]">Currency</div>
                <Sl value={editVals._cur !== undefined ? editVals._cur : (companyData.currency || "INR")} onChange={v => setEditVals(prev => ({ ...prev, _cur: v }))} options={[
                  { v: "INR", l: "INR — ₹" }, { v: "USD", l: "USD — $" }, { v: "EUR", l: "EUR — €" },
                  { v: "GBP", l: "GBP — £" }, { v: "AED", l: "AED — د.إ" }, { v: "SGD", l: "SGD — S$" },
                ]} />
              </div>
              <div>
                <div className="text-[12px] text-t3 mb-[3px]">Date Format</div>
                <Sl value={editVals._df !== undefined ? editVals._df : (companyData.date_format || "DD/MM/YYYY")} onChange={v => setEditVals(prev => ({ ...prev, _df: v }))} options={[
                  { v: "DD/MM/YYYY", l: "DD/MM/YYYY" }, { v: "MM/DD/YYYY", l: "MM/DD/YYYY" }, { v: "YYYY-MM-DD", l: "YYYY-MM-DD" },
                ]} />
              </div>
            </div>
            <div className="mb-[12px]">
              <div className="text-[12px] text-t3 mb-[3px]">Financial Year Start</div>
              <Sl value={editVals._fy !== undefined ? editVals._fy : (companyData.financial_year_start || "April")} onChange={v => setEditVals(prev => ({ ...prev, _fy: v }))} options={[
                { v: "January", l: "January (Jan–Dec)" }, { v: "April", l: "April (Apr–Mar)" }, { v: "July", l: "July (Jul–Jun)" }, { v: "October", l: "October (Oct–Sep)" },
              ]} />
            </div>
            <Bt full disabled={savingRegional} onClick={async () => {
              setSavingRegional(true);
              try {
                await saveCompany({
                  ...companyData,
                  timezone: editVals._tz || companyData.timezone || "Asia/Kolkata",
                  currency: editVals._cur || companyData.currency || "INR",
                  date_format: editVals._df || companyData.date_format || "DD/MM/YYYY",
                  financial_year_start: editVals._fy || companyData.financial_year_start || "April",
                });
                setEditVals(prev => { const n = { ...prev }; delete n._tz; delete n._cur; delete n._df; delete n._fy; return n; });
                // Push the new TZ to window immediately so the current tab reflects it.
                if (editVals._tz) window.__APP_TZ = editVals._tz;
                notify('Regional settings saved', { title: 'Saved', variant: 'success' });
              } catch (e) { /* already notified in saveCompany */ }
              finally { setSavingRegional(false); }
            }}>{savingRegional ? 'Saving…' : 'Save Regional Settings'}</Bt>
          </Cd>
          <Cd><Sec icon={Upload} title="Organization Logo" />
            <div className="flex flex-col items-center gap-3">
              <div className="p-4 bg-bg2 rounded-xl border border-b0 text-center">
                <div className="text-[12px] text-t3 mb-[6px]">CURRENT LOGO</div>
                {companyData.logo_url ? <img src={uploadUrl(companyData.logo_url)} alt="Logo" className="max-w-[160px] max-h-[80px] rounded-lg" />
                  : <div className="w-20 h-20 rounded-xl flex items-center justify-center text-2xl font-extrabold text-white mx-auto" style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>{(companyData.company_name || "HR").slice(0, 2).toUpperCase()}</div>}
              </div>
              {editVals._logoPreview ? <div className="text-center">
                <div className="text-[12px] text-t3 mb-[6px]">NEW LOGO PREVIEW</div>
                <div className="p-3 bg-white rounded-[10px] border-2 border-p inline-block mb-[10px]">
                  <img src={editVals._logoPreview} alt="preview" className="max-w-[180px] max-h-[90px] rounded-md" />
                </div>
                <div className="flex gap-[6px] justify-center flex-wrap">
                  <Bt icon={CheckCircle} disabled={savingLogo} onClick={async () => {
                    if (!editVals._logoFile) return;
                    setSavingLogo(true);
                    const fd = new FormData(); fd.append("file", editVals._logoFile);
                    try {
                      const res = await fetch(BASE + "/api/settings/logo", { method: "POST", headers: { "Authorization": "Bearer " + localStorage.getItem("hrms_token") }, body: fd });
                      const data = await res.json();
                      if (data.uploaded) {
                        setEditVals(prev => ({ ...prev, _logoPreview: null, _logoFile: null }));
                        api.get('/api/settings').then(d => onSettDataChange && onSettDataChange(d));
                        onRefresh && onRefresh();
                        notify("Logo saved", { title: "Saved", variant: "success" });
                      } else {
                        notify(data.error || "Upload failed", { title: "Error", variant: "error" });
                      }
                    } catch (err) { notify(err.message || "Upload failed", { title: "Error", variant: "error" }); }
                    finally { setSavingLogo(false); }
                  }}>{savingLogo ? "Saving…" : "Save Logo"}</Bt>
                  <Bt v="secondary" onClick={() => setEditVals(prev => ({ ...prev, _logoPreview: null, _logoFile: null }))}>Cancel</Bt>
                </div>
              </div>
                : <div
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = K.p; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = K.b1; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = K.b1; const f = e.dataTransfer.files[0]; if (f && f.type.startsWith("image/")) { const r = new FileReader(); r.onload = ev => setEditVals(prev => ({ ...prev, _logoPreview: ev.target.result, _logoFile: f })); r.readAsDataURL(f); } }}
                  className="border-2 border-dashed border-b1 rounded-xl p-6 text-center cursor-pointer bg-bg2 transition-colors w-full"
                  onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".png,.jpg,.jpeg,.svg,.webp"; inp.onchange = e => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = ev => setEditVals(prev => ({ ...prev, _logoPreview: ev.target.result, _logoFile: f })); r.readAsDataURL(f); } }; inp.click(); }}>
                  <Upload size={24} color={K.p} className="mx-auto mb-2" />
                  <div className="text-[14px] font-semibold text-t0">Drop logo here or click to browse</div>
                  <div className="text-[12px] text-t3 mt-1">PNG, JPG, SVG · Recommended: 200×60px</div>
                </div>}
            </div>
          </Cd>
        </div>
        <Cd><Sec icon={Building} title="Group Companies" />
          <div className="text-[14px] text-t3 mb-2">
            <b className="text-t0">Deactivate</b> winds a company down — its employees are flipped to Inactive, logins disabled, and new payroll runs / compliance filings / vendor bills are blocked. Historical data stays queryable for audit and statutory retention (India: 7 years). <b className="text-t0">Purge</b> is only for empty companies — same shape as employee/asset purge, requires typing the name.
          </div>
          <Tbl cols={[
            { k: "name", l: "Company", r: r => <span className="font-semibold">{r.name}</span> },
            { k: "web", l: "Website", r: r => <span className="text-bl text-[12px]">{r.website || "—"}</span> },
            { k: "emp", l: "Employees", r: r => <span className="font-bold">{r.employee_count || 0}</span> },
            { k: "st", l: "Status", r: r => <Bg c={r.is_active !== false ? K.g : K.r} sm dot>{r.is_active !== false ? "Active" : "Inactive"}</Bg> },
            { k: "act", l: "", r: r => <div className="flex gap-[3px]">
              <Bt v="ghost" s="xs" icon={Edit} onClick={() => { setEditVals(prev => ({ ...prev, _editComp: r })); setShowForm("edit_company"); }}>Edit</Bt>
              {r.is_active !== false
                ? <Bt v="secondary" s="xs" icon={X} onClick={() => setEditVals(prev => ({ ...prev, _deactivateComp: r }))}>Deactivate</Bt>
                : <Bt v="success" s="xs" icon={Check} disabled={companyBusy === r.id} onClick={async () => {
                    if (!(await confirmDialog(`Reactivate "${r.name}"?\n\nThe company becomes Active again and can accept new payroll/compliance/vendor writes. Employees stay Inactive — reactivate them individually from Employees > Danger Zone.`))) return;
                    setCompanyBusy(r.id);
                    try {
                      await api.put("/api/companies/" + r.id + "/reactivate", {});
                      refreshCompaniesBoth();
                      notify(`"${r.name}" reactivated`, { title: "Saved", variant: "success" });
                    }
                    catch (e) { notify(e.message || "Failed to reactivate company", { title: "Error", variant: "error" }); }
                    finally { setCompanyBusy(null); }
                  }}>{companyBusy === r.id ? "Reactivating…" : "Reactivate"}</Bt>}
              <Bt v="danger" s="xs" icon={X}
                disabled={r.is_active !== false}
                title={r.is_active !== false ? "Deactivate the company before you can purge it" : "Permanently delete — checks all child rows first"}
                onClick={() => setEditVals(prev => ({ ...prev, _purgeComp: r, _purgeCompText: '' }))}>Purge</Bt>
            </div> }
          ]} data={allCompanies} />
          <Bt icon={Plus} style={{ marginTop: 8 }} onClick={() => setShowForm("company")}>Add Company</Bt>
          {editVals._deactivateComp && <CompanyDeactivateModal
            company={editVals._deactivateComp}
            onClose={() => setEditVals(prev => { const n = { ...prev }; delete n._deactivateComp; return n; })}
            onDone={() => { setEditVals(prev => { const n = { ...prev }; delete n._deactivateComp; return n; }); refreshCompaniesBoth(); }}
          />}
          {editVals._purgeComp && <CompanyPurgeModal
            company={editVals._purgeComp}
            text={editVals._purgeCompText || ''}
            onTextChange={t => setEditVals(prev => ({ ...prev, _purgeCompText: t }))}
            onClose={() => setEditVals(prev => { const n = { ...prev }; delete n._purgeComp; delete n._purgeCompText; return n; })}
            onDone={() => { setEditVals(prev => { const n = { ...prev }; delete n._purgeComp; delete n._purgeCompText; return n; }); refreshCompaniesBoth(); }}
          />}
          {showForm === "company" && <FormModal title="Add Company"
            fields={[
              { key: "name", label: "Company Name", placeholder: "New Subsidiary" },
              { key: "short_code", label: "Short Code (2 letters — used in employee IDs like AABBBXXXXX)", placeholder: "e.g. ZU, BC" },
              { key: "website", label: "Website", placeholder: "https://example.com" },
            ]}
            onSubmit={async v => {
              if (!v.name || !v.name.trim()) throw new Error("Company name is required");
              const sc = (v.short_code || '').trim().toUpperCase();
              if (sc && !/^[A-Z]{2}$/.test(sc)) throw new Error('Short code must be exactly 2 letters');
              try {
                await api.post("/api/companies", { name: v.name.trim(), short_code: sc || null, website: v.website || null, parent_company_id: 1 });
                refreshCompaniesBoth();
                notify('Company added', { title: 'Added', variant: 'success' });
              } catch (e) { notify(e.message || 'Failed to add company', { title: 'Error', variant: 'error' }); throw e; }
            }}
            onClose={() => setShowForm(null)} />}
          {showForm === "edit_company" && editVals._editComp && <FormModal title={"Edit: " + editVals._editComp.name}
            initialValues={{ name: editVals._editComp.name || "", short_code: editVals._editComp.short_code || "", website: editVals._editComp.website || "" }}
            fields={[
              { key: "name", label: "Company Name" },
              { key: "short_code", label: "Short Code (2 letters — used in employee IDs)" },
              { key: "website", label: "Website", placeholder: "https://example.com" },
            ]}
            onSubmit={async v => {
              if (!v.name || !v.name.trim()) throw new Error("Company name is required");
              const sc = (v.short_code || '').trim().toUpperCase();
              if (sc && !/^[A-Z]{2}$/.test(sc)) throw new Error('Short code must be exactly 2 letters');
              try {
                await api.put("/api/companies/" + editVals._editComp.id, { name: v.name.trim(), short_code: sc || null, website: v.website || null });
                refreshCompaniesBoth();
                setEditVals(prev => ({ ...prev, _editComp: null }));
                notify('Company updated', { title: 'Saved', variant: 'success' });
              } catch (e) { notify(e.message || 'Failed to update company', { title: 'Error', variant: 'error' }); throw e; }
            }}
            onClose={() => { setShowForm(null); setEditVals(prev => ({ ...prev, _editComp: null })); }} />}
        </Cd>
      </div>;
    })()}
    {settTab === "leave_types" && <div>
      {saveMsg && <div className={`px-3 py-2 mb-[10px] rounded-lg text-[14px] font-semibold ${saveMsg.startsWith("Error") ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{saveMsg}</div>}
      <FreezeWindowConfig setSaveMsg={setSaveMsg} />
      <KpiStrip items={[
        { icon: CalendarDays, label: "Total Types",     value: sLT.length,       c: K.p  },
        { icon: CheckCircle,  label: "Active",          value: activeLT.length,  c: K.g  },
        { icon: Users,        label: "Gender-Specific", value: genderLT.length,  c: K.pu },
        { icon: Clock,        label: "One-Time",        value: oneTimeLT.length, c: K.a  },
      ]} />
      <Cd>
        <div className="flex justify-between items-center mb-3">
          <Sec icon={CalendarDays} title="Leave Types Configuration" />
          <Bt icon={Plus} s="sm" onClick={() => setLvTypeEdit({ _new: true })}>Add Leave Type</Bt>
        </div>
        <Tbl cols={[
          { k: "cd", l: "Code", r: r => <div className="flex items-center gap-[6px]"><div className="w-2 h-2 rounded-full" style={{ background: r.clr || K.p }} /><span className="font-bold text-[14px]">{r.cd}</span></div> },
          { k: "nm", l: "Name", r: r => <span className="font-semibold text-[14px]">{r.nm}</span> },
          { k: "bal", l: "Balance", r: r => <span className="font-bold text-[14px] text-p">{r.unl ? "Unlimited" : r.bal + "d"}</span> },
          { k: "accrual", l: "Accrual", r: r => <Bg c={r.accrual === "yearly" ? K.bl : r.accrual === "monthly" ? K.tl : K.a} sm>{r.accrual === "yearly" ? "Yearly" : r.accrual === "monthly" ? "Monthly " + r.mc + "d/mo" : "One-Time"}</Bg> },
          { k: "gen", l: "Gender", r: r => <span className="text-[14px] text-t2">{r.gen === "F" ? "Female Only" : r.gen === "M" ? "Male Only" : "All"}</span> },
          { k: "paid", l: "Paid", r: r => <Bg c={r.paid ? K.g : K.r} sm dot>{r.paid ? "Paid" : "Unpaid"}</Bg> },
          { k: "cf", l: "Carry Fwd", r: r => <span className={`text-[14px] ${r.cf ? "text-g" : "text-t3"}`}>{r.cf ? "Yes" : "No"}</span> },
          { k: "ret", l: "Return Track", r: r => r.ret ? <Bg c={K.pu} sm dot>Tracked</Bg> : <span className="text-[14px] text-t3">—</span> },
          { k: "active", l: "Status", r: r => <Bg c={r.active !== false ? K.g : K.r} sm dot>{r.active !== false ? "Active" : "Inactive"}</Bg> },
          { k: "act", l: "Actions", r: r => <div className="flex gap-[3px]">
            <Bt v="ghost" s="xs" icon={Edit} onClick={() => setLvTypeEdit(r)}>Edit</Bt>
            {r.active !== false ? <Bt v="ghost" s="xs" icon={X} disabled={ltBusy === r.cd} onClick={async () => { if ((await confirmDialog("Deactivate " + r.nm + "?"))) deactivateLT(r.cd); }}>{ltBusy === r.cd ? "Deactivating…" : "Deactivate"}</Bt>
              : <Bt v="success" s="xs" icon={Check} disabled={ltBusy === r.cd} onClick={async () => { setLtBusy(r.cd); try { await api.put("/api/leaves/types/" + r.cd, { is_active: true }); reloadLT(); setSaveMsg(r.nm + " activated"); setTimeout(() => setSaveMsg(""), 3000); notify(r.nm + " activated", { title: "Saved", variant: "success" }); } catch (e) { setSaveMsg("Error: " + (e.message || "Failed")); notify(e.message || "Failed to activate leave type", { title: "Error", variant: "error" }); } finally { setLtBusy(null); } }}>{ltBusy === r.cd ? "Activating…" : "Activate"}</Bt>}
          </div> }
        ]} data={sLT} />
        {sLT.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No leave types configured</div>}
      </Cd>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        <Cd style={{ borderLeft: `3px solid ${K.bl}` }}>
          <div className="text-[14px] font-bold text-t0 mb-[6px]">Yearly Accrual</div>
          <div className="text-[14px] text-t2">Balance allocated once per year at the start of the financial year. Resets annually unless carry-forward is enabled.</div>
          <div className="mt-2 flex flex-wrap gap-1">{sLT.filter(t => t.accrual === "yearly").map(t => <Bg key={t.cd} c={t.clr} sm>{t.cd} ({t.bal}d)</Bg>)}</div>
        </Cd>
        <Cd style={{ borderLeft: `3px solid ${K.tl}` }}>
          <div className="text-[14px] font-bold text-t0 mb-[6px]">Monthly Accrual</div>
          <div className="text-[14px] text-t2">Balance credited monthly (e.g., 1.5d/month = 18d/year). Good for earned leave that accumulates over time.</div>
          <div className="mt-2 flex flex-wrap gap-1">{sLT.filter(t => t.accrual === "monthly").map(t => <Bg key={t.cd} c={t.clr} sm>{t.cd} ({t.mc}d/mo)</Bg>)}{sLT.filter(t => t.accrual === "monthly").length === 0 && <span className="text-[12px] text-t3">No monthly types configured</span>}</div>
        </Cd>
        <Cd style={{ borderLeft: `3px solid ${K.a}` }}>
          <div className="text-[14px] font-bold text-t0 mb-[6px]">One-Time Allocation</div>
          <div className="text-[14px] text-t2">Balance allocated once on eligibility. Used for maternity/paternity leave. Tracks return date when enabled.</div>
          <div className="mt-2 flex flex-wrap gap-1">{sLT.filter(t => t.accrual === "one_time").map(t => <Bg key={t.cd} c={t.clr} sm>{t.cd} ({t.bal}d){t.ret ? " ↩" : ""}</Bg>)}</div>
        </Cd>
      </div>
      {lvTypeEdit && <LeaveTypeModal item={lvTypeEdit} onSave={saveLvType} onClose={() => setLvTypeEdit(null)} />}
    </div>}
    {settTab === "appraisal" && (() => {
      const cycs = settApCycles; const activeCyc = cycs.find(c => c.status === "Active");
      const reloadCycs = () => api.get('/api/appraisals/cycles').then(d => { const cycles = d.data || d || []; onApCyclesChange && onApCyclesChange(cycles); });
      const createCycle = async (v) => {
        try {
          await api.post('/api/appraisals/cycles', { cycle_name: v.cycle_name, financial_year: v.financial_year, start_month: parseInt(v.start_month) || 4, end_date: v.end_date || null, min_tenure_days: parseInt(v.min_tenure_days) || 270 });
          reloadCycs();
          notify('Appraisal cycle created', { title: 'Added', variant: 'success' });
        } catch (e) { notify(e.message || 'Failed to create cycle', { title: 'Error', variant: 'error' }); throw e; }
      };
      const activateCycle = async (id) => { setCycBusy(id); try { const r = await api.post('/api/appraisals/cycles/' + id + '/activate'); notify('Cycle activated! ' + r.created + ' eligible, ' + r.ineligible + ' ineligible, ' + r.skipped + ' already existed.', { title: 'Activated', variant: 'success' }); reloadCycs(); } catch (e) { notify(e.message || 'Error activating', { title: 'Error', variant: 'error' }); } finally { setCycBusy(null); } };
      const closeCycle = async (id) => { setCycBusy(id); try { await api.put('/api/appraisals/cycles/' + id, { status: 'Closed' }); reloadCycs(); notify('Appraisal cycle closed', { title: 'Saved', variant: 'success' }); } catch (e) { notify(e.message || 'Failed to close cycle', { title: 'Error', variant: 'error' }); } finally { setCycBusy(null); } };
      const mths = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return <div>
        {activeCyc && <Cd style={{ marginBottom: 16, padding: 14, borderLeft: `3px solid ${K.pu}` }}>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div><div className="text-[15px] font-extrabold text-t0">{activeCyc.name} — <span className="text-g">Active</span></div>
              <div className="text-[14px] text-t2 mt-[3px]">Starts: {mths[activeCyc.startMonth]} {activeCyc.startDay} · Deadline: {activeCyc.endDate || "Not set"} · Min Tenure: {activeCyc.minTenure}d</div></div>
            <div className="flex gap-[6px]">
              <Bt v="danger" s="sm" disabled={cycBusy === activeCyc.id} onClick={async () => { if ((await confirmDialog("Close this cycle?"))) closeCycle(activeCyc.id); }}>{cycBusy === activeCyc.id ? "Closing…" : "Close Cycle"}</Bt>
            </div>
          </div>
          {activeCyc.stats && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
            {[["Total", activeCyc.stats.total, K.p], ["Pending", activeCyc.stats.pending, K.a], ["Self Done", activeCyc.stats.selfDone, K.bl], ["Mgr Review", activeCyc.stats.mgrReview, K.pu], ["CH Review", activeCyc.stats.chReview, K.tl], ["Done", activeCyc.stats.done, K.g]].map(([l, v, c]) => <div key={l} className="text-center px-1 py-[6px] rounded-lg border" style={{ background: `${c}08`, borderColor: `${c}18` }}><div className="text-[17px] font-extrabold" style={{ color: c }}>{v}</div><div className="text-[12px] text-t3">{l}</div></div>)}
          </div>}
        </Cd>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Cd>
            <Sec icon={CalendarDays} title="Appraisal Cycles" />
            {cycs.map(c => <div key={c.id} className="flex justify-between items-center px-2 py-[6px] bg-bg2 rounded-lg border border-b0 mb-1">
              <div><div className="text-[14px] font-semibold text-t0">{c.name}</div><div className="text-[12px] text-t3">FY {c.fy} · {mths[c.startMonth]} · Tenure: {c.minTenure}d</div></div>
              <div className="flex items-center gap-[6px]">
                <Bg c={c.status === "Active" ? K.g : c.status === "Draft" ? K.a : K.t3} sm dot>{c.status}</Bg>
                {c.status === "Draft" && <Bt v="success" s="xs" disabled={cycBusy === c.id} onClick={() => activateCycle(c.id)}>{cycBusy === c.id ? "Activating…" : "Activate"}</Bt>}
              </div>
            </div>)}
            <Bt v="secondary" s="sm" icon={Plus} full style={{ marginTop: 8 }} onClick={() => setShowForm("appraisal_cycle")}>New Cycle</Bt>
            {showForm === "appraisal_cycle" && <FormModal title="Create Appraisal Cycle" fields={[
              { key: "cycle_name", label: "Cycle Name", placeholder: "FY 2026-27" },
              { key: "financial_year", label: "Financial Year", placeholder: "2026-27" },
              { key: "start_month", label: "Start Month", type: "select", options: [{ v: "1", l: "January" }, { v: "2", l: "February" }, { v: "3", l: "March" }, { v: "4", l: "April" }, { v: "5", l: "May" }, { v: "6", l: "June" }, { v: "7", l: "July" }, { v: "8", l: "August" }, { v: "9", l: "September" }, { v: "10", l: "October" }, { v: "11", l: "November" }, { v: "12", l: "December" }] },
              { key: "end_date", label: "Deadline Date", type: "date" },
              { key: "min_tenure_days", label: "Min Tenure (days)", placeholder: "270" },
            ]} onSubmit={createCycle} onClose={() => setShowForm(null)} />}
          </Cd>
          <Cd>
            <Sec icon={Shield} title="Approval Limits per Role" />
            <div className="text-[14px] text-t2 mb-2">
              <b>Leave</b> = max days a role can approve in one leave request. <b>Appraisal</b> = max appraisals a role can approve per cycle. <b>999</b> = unlimited (shown as ∞). Save writes to the role definition (Admin only).
            </div>
            {roles.filter(r => r.lv <= 6).map(r => {
              const laKey = '_la_' + r.id;
              const aaKey = '_aa_' + r.id;
              const laDirty = editVals[laKey] !== undefined && String(editVals[laKey]) !== String(r.la ?? 0);
              const aaDirty = editVals[aaKey] !== undefined && String(editVals[aaKey]) !== String(r.aa ?? 0);
              const dirty = laDirty || aaDirty;
              const saveRow = async () => {
                const la = parseInt(editVals[laKey] !== undefined ? editVals[laKey] : (r.la ?? 0), 10);
                const aa = parseInt(editVals[aaKey] !== undefined ? editVals[aaKey] : (r.aa ?? 0), 10);
                if (isNaN(la) || la < 0 || la > 999 || isNaN(aa) || aa < 0 || aa > 999) {
                  notify('Values must be between 0 and 999 (0 = none, 999 = unlimited)', { title: 'Error', variant: 'error' });
                  return;
                }
                setRoleBusy(r.id);
                try {
                  await api.put('/api/roles/' + r.id, { leave_approval_limit: la, appraisal_approval_limit: aa });
                  await reloadRoles();
                  setEditVals(prev => { const n = { ...prev }; delete n[laKey]; delete n[aaKey]; return n; });
                  setSaveMsg(`${r.t}: limits saved`); setTimeout(() => setSaveMsg(''), 2500);
                  notify(`${r.t}: approval limits saved`, { title: 'Saved', variant: 'success' });
                } catch (e) { notify(e.message || 'Save failed', { title: 'Error', variant: 'error' }); } finally { setRoleBusy(null); }
              };
              return <div key={r.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-[8px] py-[6px] bg-bg2 rounded-md border border-b0 mb-[3px]">
                <div className="flex items-center gap-1 min-w-0">
                  <div className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background: r.c }} />
                  <span className="text-[14px] text-t0 truncate">{r.t}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[12px] text-t3">Leave</span>
                  <input type="number" min="0" max="999" value={editVals[laKey] !== undefined ? editVals[laKey] : (r.la ?? 0)}
                         onChange={e => setEditVals(prev => ({ ...prev, [laKey]: e.target.value }))}
                         className="w-[54px] px-[6px] py-[2px] text-[14px] bg-bg1 border border-b0 rounded text-t0 text-right" />
                  <span className="text-[12px] text-t3">d</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[12px] text-t3">Appraisal</span>
                  <input type="number" min="0" max="999" value={editVals[aaKey] !== undefined ? editVals[aaKey] : (r.aa ?? 0)}
                         onChange={e => setEditVals(prev => ({ ...prev, [aaKey]: e.target.value }))}
                         className="w-[54px] px-[6px] py-[2px] text-[14px] bg-bg1 border border-b0 rounded text-t0 text-right" />
                </div>
                {dirty
                  ? <Bt s="xs" icon={Check} onClick={saveRow} disabled={roleBusy === r.id}>{roleBusy === r.id ? "Saving…" : "Save"}</Bt>
                  : <span className="text-[12px] text-t3 w-[44px] text-right">saved</span>}
              </div>;
            })}
          </Cd>
        </div>

        <Cd style={{ marginTop: 12 }}>
          <Sec icon={HelpCircle} title="Self-Appraisal Form Questions" />
          <div className="text-[14px] text-t2 mb-2">
            These questions are what every employee sees on the "Self Assessment" step of their appraisal. Reorder, edit, retire (Active toggle), or add new ones — changes apply immediately, no deploy needed.
          </div>
          {apQs.length === 0 && <div className="text-[14px] text-t3 py-2">No questions configured yet.</div>}
          {apQs.map((q, i) => {
            const move = async (dir) => {
              const j = i + dir;
              if (j < 0 || j >= apQs.length) return;
              setApQBusy(q.id);
              try {
                await Promise.all([
                  api.put('/api/appraisals/questions/' + q.id, { position: apQs[j].position }),
                  api.put('/api/appraisals/questions/' + apQs[j].id, { position: q.position }),
                ]);
                reloadApQs();
              } catch (e) { notify(e.message || 'Failed to reorder', { title: 'Error', variant: 'error' }); }
              finally { setApQBusy(null); }
            };
            const toggleActive = async () => {
              setApQBusy(q.id);
              try { await api.put('/api/appraisals/questions/' + q.id, { active: !q.active }); reloadApQs(); }
              catch (e) { notify(e.message || 'Failed to update', { title: 'Error', variant: 'error' }); }
              finally { setApQBusy(null); }
            };
            const removeQ = async () => {
              if (!(await confirmDialog('Delete this question? Past answers already submitted will keep the text but lose the link to it.'))) return;
              setApQBusy(q.id);
              try { await api.delete('/api/appraisals/questions/' + q.id); reloadApQs(); notify('Question deleted', { title: 'Deleted', variant: 'success' }); }
              catch (e) { notify(e.message || 'Failed to delete', { title: 'Error', variant: 'error' }); }
              finally { setApQBusy(null); }
            };
            return <div key={q.id} className="flex items-center gap-2 px-2 py-[8px] bg-bg2 rounded-lg border border-b0 mb-1">
              <div className="flex flex-col gap-[2px]">
                <button disabled={i === 0 || apQBusy === q.id} onClick={() => move(-1)} className="bg-transparent border-0 text-t3 cursor-pointer disabled:opacity-30"><ArrowUp size={12} /></button>
                <button disabled={i === apQs.length - 1 || apQBusy === q.id} onClick={() => move(1)} className="bg-transparent border-0 text-t3 cursor-pointer disabled:opacity-30"><ArrowDown size={12} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] text-t0 truncate">{q.label}</div>
                <div className="text-[12px] text-t3">{q.question_type}{q.required ? ' · required' : ' · optional'}</div>
              </div>
              <Bg c={q.active ? K.g : K.t3} sm dot style={{ cursor: 'pointer' }} onClick={toggleActive}>{q.active ? 'Active' : 'Retired'}</Bg>
              <Bt v="ghost" s="xs" icon={Edit} onClick={() => setShowForm({ _type: 'appraisal_question', _q: q })} />
              <Bt v="ghost" s="xs" icon={Trash2} disabled={apQBusy === q.id} onClick={removeQ} />
            </div>;
          })}
          <Bt v="secondary" s="sm" icon={Plus} full style={{ marginTop: 8 }} onClick={() => setShowForm({ _type: 'appraisal_question', _q: null })}>Add Question</Bt>
          {showForm?._type === 'appraisal_question' && <FormModal
            title={showForm._q ? 'Edit Question' : 'Add Question'}
            initialValues={showForm._q ? { label: showForm._q.label, question_type: showForm._q.question_type, required: showForm._q.required ? 'true' : 'false' } : { question_type: 'textarea', required: 'true' }}
            fields={[
              { key: 'label', label: 'Question Text', placeholder: 'e.g. What was your best contribution this year?' },
              { key: 'question_type', label: 'Answer Type', type: 'select', options: [{ v: 'textarea', l: 'Long text' }, { v: 'text', l: 'Short text' }, { v: 'select', l: 'Dropdown' }, { v: 'file', l: 'File upload' }] },
              { key: 'required', label: 'Required', type: 'select', options: [{ v: 'true', l: 'Yes' }, { v: 'false', l: 'No' }] },
            ]}
            onSubmit={async (v) => {
              const payload = { label: v.label, question_type: v.question_type, required: v.required === 'true' };
              if (!payload.label) { notify('Question text is required', { title: 'Error', variant: 'error' }); throw new Error('Question text is required'); }
              try {
                if (showForm._q) await api.put('/api/appraisals/questions/' + showForm._q.id, payload);
                else await api.post('/api/appraisals/questions', payload);
                reloadApQs();
                notify(showForm._q ? 'Question updated' : 'Question added', { title: 'Saved', variant: 'success' });
              } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); throw e; }
            }}
            onClose={() => setShowForm(null)}
          />}
        </Cd>
      </div>;
    })()}
    {settTab === "compliance" && <ComplianceSettingsTab initial={cmpData?.configs || CD?.configs || {}} onSaved={() => onRefresh && onRefresh()} />}
    {settTab === "payroll" && <PayrollSettingsTab companies={companies} />}
    {settTab === "salary" && <SalaryStructureManager />}
    {settTab === "expenses" && <ExpenseSettingsTab />}
    {settTab === "recruitment" && <RecruitmentOptionsCard />}
    {settTab === "email" && <EmailSettingsTab />}
    {settTab === "holidays" && (() => {
      const holOffices = holData.length > 0 ? holData : [];
      const loadHols = () => api.get('/api/holidays').then(d => onHolDataChange && onHolDataChange(d.data || d || []));
      const addHoliday = async (v) => {
        const ids = (Array.isArray(v.office_ids) ? v.office_ids : []).map(Number).filter(Boolean);
        if (!ids.length) { const e = new Error('Select at least one office'); notify(e.message, { title: 'Error', variant: 'error' }); throw e; }
        // One request per office (the API creates a holiday for a single office); run them together and report any that failed.
        const results = await Promise.allSettled(ids.map(id => api.post('/api/holidays', { office_id: id, name: v.name, date: v.date || null, holiday_type: v.holiday_type || 'National', is_optional: v.is_optional === 'true' })));
        loadHols();
        const failed = results.map((r, i) => r.status === 'rejected' ? { id: ids[i], msg: r.reason?.message || 'failed' } : null).filter(Boolean);
        if (failed.length === ids.length) { const e = new Error(failed[0].msg); notify(e.message || 'Failed to add holiday', { title: 'Error', variant: 'error' }); throw e; }
        const nm = (id) => (holOffices.find(o => o.id === id)?.nm || holOffices.find(o => o.id === id)?.name || id);
        if (failed.length) notify(`Added to ${ids.length - failed.length} office(s). Failed for: ${failed.map(f => nm(f.id)).join(', ')}`, { title: 'Partly added', variant: 'info' });
        else notify(ids.length > 1 ? `Holiday added to ${ids.length} offices` : 'Holiday added', { title: 'Added', variant: 'success' });
      };
      const deleteHol = async (id) => {
        setOfcBusy(true);
        try { await api.del('/api/holidays/' + id); loadHols(); notify('Holiday removed', { title: 'Deleted', variant: 'success' }); }
        catch (e) { notify(e.message || 'Failed to remove holiday', { title: 'Error', variant: 'error' }); }
        finally { setOfcBusy(false); }
      };
      const mths = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const fmtDt = (d) => { if (!d) return "—"; const [y, m, dd] = d.split("-"); return dd + " " + mths[parseInt(m)] + " " + y; };
      return <div>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <div><div className="text-[14px] font-bold text-t0">Office-wise Holiday Calendar</div><div className="text-[14px] text-t3">Manage holidays per office location</div></div>
          {can('settings', 'holidays', 'holiday_calendar', 'edit') && <Bt icon={Plus} s="sm" onClick={() => setShowForm("holiday")}>Add Holiday</Bt>}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {holOffices.map(o => <Cd key={o.id}>
            <div className="flex items-center gap-2 mb-[10px] flex-wrap">
              <Building size={16} color={K.p} />
              <div><div className="font-bold text-[15px] text-t0">{o.nm || o.name}</div><div className="text-[14px] text-t3">{o.city} · {o.emp || 0} employees</div></div>
              <Bg c={K.bl} sm style={{ marginLeft: "auto" }}>{(o.hols || []).length} holidays</Bg>
            </div>
            {(o.hols || []).map(h => <div key={h.id || h.name} className="flex items-center justify-between px-[6px] py-1 bg-bg2 rounded-md border border-b0 mb-[3px]">
              <div className="flex items-center gap-[6px] min-w-0">
                <CalendarDays size={10} color={h.optional ? K.a : K.g} />
                <span className="text-[14px] text-t0 truncate">{h.name}</span>
                {h.optional && <Bg c={K.a} sm>Optional</Bg>}
              </div>
              <div className="flex items-center gap-[6px]">
                <span className="text-[12px] text-t3">{fmtDt(h.date)}</span>
                <Bg c={h.type === "National" ? K.bl : h.type === "Regional" ? K.pu : K.tl} sm>{h.type || "National"}</Bg>
                {h.id && can('settings', 'holidays', 'holiday_calendar', 'delete') && <button disabled={ofcBusy} onClick={async () => { if ((await confirmDialog("Remove " + h.name + "?"))) deleteHol(h.id); }} className="bg-transparent border-0 text-r cursor-pointer text-[14px] disabled:opacity-50">x</button>}
              </div>
            </div>)}
          </Cd>)}
        </div>
        {showForm === "holiday" && <FormModal title="Add Holiday" fields={[
          { key: "office_ids", label: "Offices (select one or more)", type: "multi-select", placeholder: "Select offices…", searchPlaceholder: "Search offices…", options: holOffices.map(o => ({ v: String(o.id), l: o.nm || o.name })) },
          { key: "name", label: "Holiday Name", placeholder: "Republic Day" },
          { key: "date", label: "Date", type: "date" },
          { key: "holiday_type", label: "Type", type: "select", options: [{ v: "National", l: "National" }, { v: "Regional", l: "Regional" }, { v: "Restricted", l: "Restricted" }] },
          { key: "is_optional", label: "Optional?", type: "select", options: [{ v: "false", l: "No — Mandatory" }, { v: "true", l: "Yes — Optional/Restricted" }] },
        ]} onSubmit={addHoliday} onClose={() => setShowForm(null)} />}
      </div>;
    })()}
    {settTab === "offices" && (() => {
      const ofcList = offices;
      const reloadOfc = () => api.get('/api/offices').then(d => onOfficesChange && onOfficesChange(Array.isArray(d) ? d : d.data || []));
      return <div>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <div><div className="text-[14px] font-bold text-t0">Work Locations / Offices</div><div className="text-[14px] text-t3">Manage office locations. These appear in employee profile Work Location dropdown.</div></div>
          {can('settings', 'offices', 'offices_list', 'edit') && <Bt icon={Plus} s="sm" onClick={() => setShowForm("add_office")}>Add Office</Bt>}
        </div>
        {ofcList.map(o => <Cd key={o.id} style={{ marginBottom: 8 }}>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-[10px] min-w-0">
              <div className="w-10 h-10 rounded-[10px] bg-pg flex items-center justify-center flex-shrink-0"><Building size={18} color={K.p} /></div>
              <div className="min-w-0">
                <div className="text-[15px] font-bold text-t0 truncate">{o.name}</div>
                <div className="flex gap-2 mt-[3px] flex-wrap">
                  <span className="text-[14px] text-t2">{o.city}</span>
                  {o.address && <span className="text-[14px] text-t3">· {o.address}</span>}
                  <Bg c={K.bl} sm>{o.employee_count || 0} employees</Bg>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {can('settings', 'offices', 'offices_list', 'edit') && <Bt v="ghost" s="sm" icon={Edit} onClick={() => { setEditVals({ _ofcId: o.id, name: o.name, city: o.city, address: o.address || "", radius_meters: o.radius_meters || 200, short_code: o.short_code || "" }); setShowForm("edit_office"); }}>Edit</Bt>}
              {can('settings', 'offices', 'offices_list', 'delete') && <Bt v="danger" s="sm" icon={Trash2} onClick={() => { setDeleteOfficeTarget(o); setDeleteOfficeConfirm(""); }}>Delete</Bt>}
            </div>
          </div>
        </Cd>)}
        {ofcList.length === 0 && <Cd><div className="text-center p-[30px] text-t3">No offices configured. Add your first office location.</div></Cd>}
        {deleteOfficeTarget && (
          <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[300] flex items-center justify-center p-3" onMouseDown={() => !deleteOfficeBusy && setDeleteOfficeTarget(null)}>
            <div className="animate-modalIn bg-bg1 border border-r/40 rounded-2xl p-5 w-full max-w-[824px] shadow-[0_20px_60px_rgba(200,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-2">
                <Trash2 size={18} className="text-r" />
                <span className="text-[17px] font-extrabold text-r">Delete {deleteOfficeTarget.name}?</span>
              </div>
              <div className="text-[14px] text-t1 leading-[1.6] mb-3">
                This will <b className="text-r">permanently delete</b> the office and every employee, candidate, onboarding record, recruitment requisition, asset, and attendance record tied to it — including employees currently Active (full payroll/attendance/leave history goes with them). A JSON backup of everything is written to <code className="bg-bg2 px-1 rounded text-[14px]">server/backups/deleted-offices/</code> before the delete runs, but this cannot be undone from the app.
              </div>
              <div className="text-[14px] text-t2 mb-1">
                Type <b className="text-t0 font-mono">{deleteOfficeTarget.name}</b> to confirm:
              </div>
              <input
                type="text"
                value={deleteOfficeConfirm}
                onChange={e => setDeleteOfficeConfirm(e.target.value)}
                placeholder={deleteOfficeTarget.name}
                className="w-full bg-bg2 border border-b1 rounded-lg px-3 py-2 text-[15px] font-mono text-t0 focus:outline-none focus:border-r mb-3"
                autoFocus
              />
              <div className="flex gap-2">
                <Bt v="secondary" full disabled={deleteOfficeBusy} onClick={() => setDeleteOfficeTarget(null)}>Cancel</Bt>
                <Bt v="danger" full disabled={deleteOfficeConfirm !== deleteOfficeTarget.name || deleteOfficeBusy} onClick={async () => {
                  setDeleteOfficeBusy(true);
                  try {
                    const r = await api.del(`/api/offices/${deleteOfficeTarget.id}`, { confirmation: deleteOfficeTarget.name });
                    notify(`${deleteOfficeTarget.name} deleted — ${r.counts.employees} employee(s), ${r.counts.candidates} candidate(s), ${r.counts.onboarding} onboarding record(s) purged. Backup: ${r.backup}`, { title: "Deleted", variant: "success" });
                    setDeleteOfficeTarget(null);
                    reloadOfc();
                  } catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
                  finally { setDeleteOfficeBusy(false); }
                }}>
                  {deleteOfficeBusy ? "Deleting…" : "Delete permanently"}
                </Bt>
              </div>
            </div>
          </div>
        )}
        {showForm === "add_office" && <FormModal title="Add Office" fields={[
          { key: "name", label: "Office Name", placeholder: "Tech Park — Bangalore" },
          { key: "city", label: "City", placeholder: "Bangalore" },
          { key: "short_code", label: "Short Code (3 letters — used in employee IDs like AABBBXXXXX)", placeholder: "e.g. BAN" },
          { key: "address", label: "Address", placeholder: "Whitefield Phase 2" },
        ]} onSubmit={async v => {
          if (!v.name || !v.city) { notify("Name and city required", { title: "Error", variant: "error" }); return; }
          const sc = (v.short_code || '').trim().toUpperCase();
          if (sc && !/^[A-Z]{3}$/.test(sc)) { notify('Short code must be exactly 3 letters', { title: "Error", variant: "error" }); return; }
          try {
            await api.post("/api/offices", { ...v, short_code: sc || null });
            reloadOfc();
            notify('Office added', { title: 'Added', variant: 'success' });
          } catch (e) { notify(e.message || 'Failed to add office', { title: 'Error', variant: 'error' }); throw e; }
        }} onClose={() => setShowForm(null)} />}
        {showForm === "edit_office" && <FormModal title="Edit Office"
          initialValues={{ name: editVals.name || "", city: editVals.city || "", short_code: editVals.short_code || "", address: editVals.address || "" }}
          fields={[
            { key: "name", label: "Office Name" },
            { key: "city", label: "City" },
            { key: "short_code", label: "Short Code (3 letters — used in employee IDs)" },
            { key: "address", label: "Address" },
          ]} onSubmit={async v => {
            const sc = (v.short_code || '').trim().toUpperCase();
            if (sc && !/^[A-Z]{3}$/.test(sc)) { notify('Short code must be exactly 3 letters', { title: "Error", variant: "error" }); return; }
            try {
              await api.put("/api/offices/" + editVals._ofcId, { name: v.name || editVals.name, city: v.city || editVals.city, address: v.address || editVals.address, short_code: sc || null });
              reloadOfc(); setEditVals({});
              notify('Office updated', { title: 'Saved', variant: 'success' });
            } catch (e) { notify(e.message || 'Failed to update office', { title: 'Error', variant: 'error' }); throw e; }
          }} onClose={() => { setShowForm(null); setEditVals({}); }} />}
      </div>;
    })()}
    {settTab === "assets" && (() => {
      const reloadCats = () => api.get('/api/assets/categories').then(d => onAssetCatsChange && onAssetCatsChange(d || []));
      const reloadGroups = () => api.get('/api/assets/type-groups').then(d => onAssetTypeGroupsChange && onAssetTypeGroupsChange(d || []));
      const groupsById = Object.fromEntries((assetTypeGroups || []).map(g => [g.id, g]));
      const flash = (text, kind = "ok") => { setSaveMsg(kind === "ok" ? text : "Error: " + text); setTimeout(() => setSaveMsg(""), 3500); };

      const saveGroup = async (vals) => {
        try {
          if (vals._id) {
            await api.put('/api/assets/type-groups/' + vals._id, { name: vals.name, sort_order: parseInt(vals.sort_order) || 0 });
            flash(vals.name + " saved");
            notify(vals.name + " saved", { title: "Saved", variant: "success" });
          } else {
            await api.post('/api/assets/type-groups', { name: vals.name, sort_order: parseInt(vals.sort_order) || (assetTypeGroups.length + 1) });
            flash("Category created");
            notify("Asset category created", { title: "Added", variant: "success" });
          }
          await reloadGroups();
        } catch (e) { flash(e.message || "Save failed", "err"); notify(e.message || "Failed to save category", { title: "Error", variant: "error" }); throw e; }
      };
      const deleteGroup = async (g) => {
        if (!(await confirmDialog(`Delete category "${g.name}"? Asset types still under it will block this.`))) return;
        setAssetBusy('group:' + g.id);
        try { await api.del('/api/assets/type-groups/' + g.id); reloadGroups(); flash(g.name + " deleted"); notify(g.name + " deleted", { title: "Deleted", variant: "success" }); }
        catch (e) { flash(e.message || "Cannot delete", "err"); notify(e.message || "Cannot delete category", { title: "Error", variant: "error" }); }
        finally { setAssetBusy(null); }
      };

      const saveType = async (vals) => {
        try {
          if (vals._cd) {
            await api.put('/api/assets/categories/' + vals._cd, {
              name: vals.name, group_id: parseInt(vals.group_id),
              depreciation_years: parseInt(vals.depreciation_years) || 3, color: vals.color || "#5F6CF0",
            });
            flash(vals.name + " saved");
            notify(vals.name + " saved", { title: "Saved", variant: "success" });
          } else {
            await api.post('/api/assets/categories', {
              code: (vals.code || "").toUpperCase(), name: vals.name, group_id: parseInt(vals.group_id),
              depreciation_years: parseInt(vals.depreciation_years) || 3, color: vals.color || "#5F6CF0",
            });
            flash("Asset type created");
            notify("Asset type created", { title: "Added", variant: "success" });
          }
          await reloadCats();
        } catch (e) { flash(e.message || "Save failed", "err"); notify(e.message || "Failed to save asset type", { title: "Error", variant: "error" }); throw e; }
      };
      const deleteType = async (t) => {
        if (!(await confirmDialog(`Delete asset type "${t.nm}"? Assets or requests using it will block this.`))) return;
        setAssetBusy('type:' + t.cd);
        try { await api.del('/api/assets/categories/' + t.cd); reloadCats(); flash(t.nm + " deleted"); notify(t.nm + " deleted", { title: "Deleted", variant: "success" }); }
        catch (e) { flash(e.message || "Cannot delete", "err"); notify(e.message || "Cannot delete asset type", { title: "Error", variant: "error" }); }
        finally { setAssetBusy(null); }
      };

      return <div>
        {saveMsg && <div className={`px-3 py-2 mb-[10px] rounded-lg text-[14px] font-semibold ${saveMsg.startsWith("Error") ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{saveMsg}</div>}

        <KpiStrip items={[
          { icon: Package,        label: "Categories",  value: assetTypeGroups.length,                              c: K.p  },
          { icon: Package,        label: "Asset Types", value: assetCats.length,                                    c: K.bl },
          { icon: CheckCircle,    label: "Mapped",      value: assetCats.filter(c => c.groupId).length,             c: K.g  },
          { icon: AlertTriangle,  label: "Unmapped",    value: assetCats.filter(c => !c.groupId).length,            c: K.a  },
        ]} />

        <Cd style={{ marginBottom: 12 }}>
          <div className="flex justify-between items-center mb-3">
            <Sec icon={Package} title="Categories" />
            <Bt icon={Plus} s="sm" onClick={() => setEditVals({ _assetGroupNew: true, name: "", sort_order: (assetTypeGroups.length + 1) })}>Add Category</Bt>
          </div>
          <div className="text-[14px] text-t3 mb-2">Top-level grouping shown when adding a new asset type. Examples: Electronics, Cards, Phone No, Furniture, Vehicles.</div>
          <Tbl cols={[
            { k: "name", l: "Category", r: r => <span className="font-semibold text-[14px] text-t0">{r.name}</span> },
            { k: "type_count", l: "Asset Types", r: r => <Bg c={r.type_count > 0 ? K.p : K.t3} sm>{r.type_count}</Bg> },
            { k: "sort", l: "Sort", r: r => <span className="text-[14px] text-t3">{r.sort_order}</span> },
            { k: "act", l: "Actions", r: r => <div className="flex gap-[3px]">
              <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEditVals({ _assetGroupId: r.id, name: r.name, sort_order: r.sort_order })}>Edit</Bt>
              <Bt v="ghost" s="xs" icon={Trash2} onClick={() => deleteGroup(r)} disabled={r.type_count > 0 || assetBusy === 'group:' + r.id}>{assetBusy === 'group:' + r.id ? 'Deleting…' : 'Delete'}</Bt>
            </div> }
          ]} data={assetTypeGroups} />
          {assetTypeGroups.length === 0 && <div className="text-center p-[20px] text-t3 text-[14px]">No categories yet</div>}
        </Cd>

        <Cd>
          <div className="flex justify-between items-center mb-3">
            <Sec icon={Package} title="Asset Types" />
            <Bt icon={Plus} s="sm" onClick={() => setEditVals({ _assetTypeNew: true, code: "", name: "", group_id: assetTypeGroups[0]?.id || "", depreciation_years: 3, color: "#5F6CF0" })} disabled={!assetTypeGroups.length}>Add Asset Type</Bt>
          </div>
          <div className="text-[14px] text-t3 mb-2">These appear in the "Asset Type" dropdown when adding a new asset.</div>
          <Tbl cols={[
            { k: "cd", l: "Code", r: r => <div className="flex items-center gap-[6px]"><div className="w-2 h-2 rounded-full" style={{ background: r.clr || K.p }} /><span className="font-bold text-[14px]">{r.cd}</span></div> },
            { k: "nm", l: "Name", r: r => <span className="font-semibold text-[14px]">{r.nm}</span> },
            { k: "group", l: "Category", r: r => r.groupId ? <Bg c={K.bl} sm>{groupsById[r.groupId]?.name || "—"}</Bg> : <span className="text-[14px] text-a">Unmapped</span> },
            { k: "dep", l: "Depreciation", r: r => <span className="text-[14px] text-t2">{r.dep || 0} yrs</span> },
            { k: "color", l: "Color", r: r => <div className="flex items-center gap-[5px]"><div className="w-4 h-4 rounded" style={{ background: r.clr || "#5F6CF0" }} /><span className="text-[14px] text-t3">{r.clr}</span></div> },
            { k: "act", l: "Actions", r: r => <div className="flex gap-[3px]">
              <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEditVals({ _assetTypeCd: r.cd, name: r.nm, group_id: r.groupId || "", depreciation_years: r.dep || 3, color: r.clr || "#5F6CF0" })}>Edit</Bt>
              <Bt v="ghost" s="xs" icon={Trash2} onClick={() => deleteType(r)} disabled={assetBusy === 'type:' + r.cd}>{assetBusy === 'type:' + r.cd ? 'Deleting…' : 'Delete'}</Bt>
            </div> }
          ]} data={assetCats} />
          {assetCats.length === 0 && <div className="text-center p-[20px] text-t3 text-[14px]">No asset types yet</div>}
        </Cd>

        {(editVals._assetGroupNew || editVals._assetGroupId) && <FormModal
          title={editVals._assetGroupId ? "Edit Category" : "Add Category"}
          fields={[
            { key: "name", label: "Name", placeholder: "e.g. Electronics" },
            { key: "sort_order", label: "Sort Order", type: "number", placeholder: "1" },
          ]}
          initialValues={{ name: editVals.name, sort_order: editVals.sort_order }}
          onSubmit={async v => { await saveGroup({ _id: editVals._assetGroupId, name: v.name || editVals.name, sort_order: v.sort_order || editVals.sort_order }); setEditVals({}); }}
          onClose={() => setEditVals({})}
        />}

        {(editVals._assetTypeNew || editVals._assetTypeCd) && <FormModal
          title={editVals._assetTypeCd ? "Edit Asset Type" : "Add Asset Type"}
          fields={[
            ...(editVals._assetTypeCd ? [] : [{ key: "code", label: "Code (max 10, A-Z)", placeholder: "LAPTOP" }]),
            { key: "name", label: "Name", placeholder: "Laptop" },
            { key: "group_id", label: "Category", type: "select", options: assetTypeGroups.map(g => ({ v: String(g.id), l: g.name })) },
            { key: "depreciation_years", label: "Depreciation (years)", type: "number", placeholder: "3" },
            { key: "color", label: "Color (hex)", placeholder: "#5F6CF0" },
          ]}
          initialValues={{
            code: editVals.code || "", name: editVals.name || "",
            group_id: editVals.group_id ? String(editVals.group_id) : "",
            depreciation_years: editVals.depreciation_years || 3,
            color: editVals.color || "#5F6CF0",
          }}
          onSubmit={async v => {
            await saveType({
              _cd: editVals._assetTypeCd,
              code: v.code || editVals.code,
              name: v.name || editVals.name,
              group_id: v.group_id || editVals.group_id,
              depreciation_years: v.depreciation_years || editVals.depreciation_years,
              color: v.color || editVals.color,
            });
            setEditVals({});
          }}
          onClose={() => setEditVals({})}
        />}
      </div>;
    })()}
    {settTab === "separation" && <SeparationSettingsTab role={editVals._role || ""} />}
    {settTab === "access" && (() => {
      const loadMatrix = () => api.get('/api/access-control/roles').then(d => setRoleMatrix(d.data || d)).catch(console.error);
      if (!roleMatrix) loadMatrix();
      const sysRoles = ["hr_head", "centre_head", "centre_hr", "recruiter", "accounts", "employee"];
      const roleLabels = { hr_head: "HR Head", centre_head: "Centre Head", centre_hr: "Centre HR", manager: "Manager", recruiter: "Recruiter", accounts: "Accounts", employee: "Employee" };
      const allModules = ["dashboard", "myprofile", "attendance", "leaves", "appraisal", "payroll", "expenses", "assets", "documents", "hrcorner", "separation", "employees", "org", "onboarding", "recruitment", "compliance", "settings"];
      const modLabels = { dashboard: "Dashboard", myprofile: "My Profile", attendance: "Attendance", leaves: "Leaves", appraisal: "Appraisal", payroll: "Payroll", expenses: "Expenses", assets: "Assets", helpdesk: "Helpdesk", documents: "Policies", hrcorner: "HR Corner", separation: "Separation", employees: "Employees", org: "Organization", onboarding: "Onboarding", recruitment: "Recruitment", compliance: "Compliance", settings: "Settings" };
      const togglePerm = async (sRole, mod, field, current) => {
        const key = `${sRole}|${mod}|${field}`;
        const cur = roleMatrix?.[sRole]?.[mod] || { view: false, edit: false, approve: false };
        const upd = { ...cur, [field]: !current };
        setPermBusy(key);
        try {
          await api.put('/api/access-control/roles', { system_role: sRole, module: mod, can_view: upd.view, can_edit: upd.edit, can_approve: upd.approve });
          loadMatrix();
        } catch (e) { notify(e.message || 'Failed to update access', { title: 'Error', variant: 'error' }); }
        finally { setPermBusy(null); }
      };
      return <div>
        <div className="text-[14px] font-bold text-t0 mb-1">Role-wise Module Access</div>
        <div className="text-[14px] text-t3 mb-[14px]">Define which modules each role can access. Admin always has full access. Per-employee overrides can be set from Employee Profile &gt; Settings tab.</div>
        <Cd style={{ overflow: "auto" }}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead><tr className="bg-bg2">
                <th className="px-[10px] py-2 text-left text-t3 text-[12px] font-semibold uppercase border-b border-b0 min-w-[120px]">Module</th>
                {sysRoles.map(r => <th key={r} className="px-[6px] py-2 text-center text-t0 text-[12px] font-semibold border-b border-b0 min-w-[80px]">{roleLabels[r]}</th>)}
              </tr></thead>
              <tbody>{allModules.map(mod => <tr key={mod} className="border-b border-b0">
                <td className="px-[10px] py-[6px] font-semibold text-t0">{modLabels[mod]}</td>
                {sysRoles.map(r => { const p = roleMatrix?.[r]?.[mod]; const isOn = p?.view !== false; const busy = permBusy === `${r}|${mod}|view`;
                  return <td key={r} className="p-[6px] text-center">
                    <div onClick={() => { if (!busy) togglePerm(r, mod, "view", isOn); }} className="w-9 h-5 rounded-[10px] cursor-pointer relative inline-block transition-colors" style={{ background: isOn ? K.g : K.b2, opacity: busy ? 0.5 : 1, pointerEvents: busy ? "none" : "auto" }}>
                      <div className="w-[14px] h-[14px] rounded-[7px] bg-white absolute top-[3px] transition-[left] shadow-[0_1px_2px_rgba(0,0,0,0.2)]" style={{ left: isOn ? 19 : 3 }} />
                    </div>
                  </td>;
                })}
              </tr>)}</tbody>
            </table>
          </div>
        </Cd>
        <div className="mt-3 text-[14px] text-t3">Changes are saved automatically. To set per-employee overrides or centre access, go to Employees &gt; Employee Profile &gt; Settings tab.</div>
      </div>;
    })()}
    {settTab === "permissions" && <PermissionsManager />}
    {settTab === "security" && <SecuritySettingsTab />}
    {settTab === "deletion_log" && <DeletionLogTab />}
  </div>;
};

// Compliance settings — statutory rate configuration for the payroll
// module. What the tab is FOR:
//   * When the government changes a rate (e.g. PF wage ceiling raised),
//     the CHRO/HR updates it here without a deploy.
//   * The payroll cycle reads these values when computing PF/ESI/TDS/PT
//     deductions on every payslip, so a wrong number here shows up on
//     every employee's next payslip. Take audit seriously.
//   * All edits land in audit_log with the admin's username.
// Was read-only display — reworked to editable cards, each with its own
// Save. Backend: PUT /api/compliance/config/:key (admin only, existing).
// Company deactivate confirmation — shows an /impact preview (counts of
// every child row this action will affect) BEFORE HR approves. Employees
// are auto-cascaded to Inactive by the backend; the preview surfaces
// that so it's not a surprise.
const CompanyDeactivateModal = ({ company, onClose, onDone }) => {
  const [impact, setImpact] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    api.get('/api/companies/' + company.id + '/impact')
      .then(d => setImpact(d.impact || {}))
      .catch(() => setImpact({}));
  }, [company.id]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.put('/api/companies/' + company.id + '/deactivate', {});
      notify(`Company "${company.name}" deactivated. ${r.employees_deactivated || 0} employee(s) set Inactive, ${r.logins_disabled || 0} login(s) disabled.`, { title: 'Deactivated', variant: 'success' });
      onDone();
    } catch (e) { setErr(e.message || 'Deactivate failed'); notify(e.message || 'Failed to deactivate company', { title: 'Error', variant: 'error' }); setBusy(false); }
  };

  const row = (label, n, warn = false) =>
    <div key={label} className={`flex justify-between py-[3px] text-[14px] ${warn && n > 0 ? 'text-r font-semibold' : 'text-t2'}`}>
      <span>{label}</span><span className="font-mono">{n}</span>
    </div>;

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[300] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[768px] shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} color={K.a} />
          <span className="text-[17px] font-extrabold text-t0">Deactivate {company.name}?</span>
        </div>
        <div className="text-[14px] text-t2 leading-[1.6] mb-3">
          The company will be marked <b className="text-a">Inactive</b>. Historical dashboards and exports keep working. New payroll runs, compliance filings, and vendor bills for this company will be <b className="text-t0">blocked</b>.
        </div>
        {impact == null ? (
          <div className="text-[14px] text-t3 py-2">Loading impact preview…</div>
        ) : (
          <div className="mb-3 p-3 bg-bg2 border border-b0 rounded-lg">
            <div className="text-[14px] font-bold text-t0 mb-2">This will affect:</div>
            {row('Active employees (→ Inactive, logins disabled)', impact.active_employees || 0, true)}
            {row('Inactive employees (unchanged)',                 impact.inactive_employees || 0)}
            {row('Offices', impact.offices || 0)}
            {row('Assets',  impact.assets  || 0)}
            {row('Vendor bills',       impact.vendor_bills       || 0)}
            {row('Expense claims',     impact.expense_claims     || 0)}
            {row('Payroll runs',       impact.payroll_runs       || 0)}
            {row('Payroll slips',      impact.payroll_slips      || 0)}
            {row('Compliance filings', impact.compliance_filings || 0)}
            {row('Deductions',         impact.deductions         || 0)}
          </div>
        )}
        <div className="text-[14px] text-t3 mb-3">
          Reactivating the company later brings it back to Active but does <b className="text-t0">not</b> auto-reactivate employees — you'll need to reactivate each individually.
        </div>
        {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
        <div className="flex gap-2">
          <Bt v="secondary" full onClick={onClose} disabled={busy}>Cancel</Bt>
          <Bt v="danger" full onClick={submit} disabled={busy}>{busy ? 'Deactivating…' : 'Deactivate Company'}</Bt>
        </div>
      </div>
    </div>
  );
};

// Company purge confirmation — mirror of employee / asset purge.
// Refuses on the backend unless the company is Inactive AND has zero
// child rows; the modal previews those counts so admin can see the
// cleanup checklist BEFORE trying (payroll runs to archive, filings
// to close, etc.).
const CompanyPurgeModal = ({ company, text, onTextChange, onClose, onDone }) => {
  const [impact, setImpact] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    api.get('/api/companies/' + company.id + '/impact')
      .then(d => setImpact(d.impact || {}))
      .catch(() => setImpact({}));
  }, [company.id]);

  const canPurge = impact != null && (impact.total || 0) === 0 && text === company.name;
  const submit = async () => {
    if (!canPurge) return;
    setBusy(true); setErr('');
    try {
      const r = await api.post('/api/companies/' + company.id + '/purge', { confirmation: company.name });
      notify(`Company "${company.name}" purged. Backup: ${r.backup}`, { title: 'Purged', variant: 'success' });
      onDone();
    } catch (e) { setErr(e.message || 'Purge failed'); notify(e.message || 'Failed to purge company', { title: 'Error', variant: 'error' }); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[300] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border rounded-2xl p-5 w-full max-w-[768px] shadow-[0_20px_60px_rgba(200,0,0,0.25)]"
           style={{ borderColor: `${K.r}55` }}
           onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} color={K.r} />
          <span className="text-[17px] font-extrabold text-r">Purge {company.name}?</span>
        </div>
        <div className="text-[14px] text-t1 leading-[1.6] mb-3">
          Permanently deletes the company row. A JSON backup is written to <code className="bg-bg2 px-1 rounded text-[14px]">server/backups/purged-companies/</code> first. Cannot be undone.
        </div>
        {impact == null ? (
          <div className="text-[14px] text-t3 py-2">Loading impact preview…</div>
        ) : (impact.total || 0) > 0 ? (
          <div className="mb-3 p-3 bg-rb border border-rd rounded-lg text-[14px]">
            <div className="text-r font-bold mb-1">Purge is blocked</div>
            <div className="text-t1 mb-2">The company still has {impact.total} related record(s). Purge is only for empty companies — keep it Inactive to preserve history:</div>
            <ul className="text-[14px] text-t2 list-disc pl-4">
              {Object.entries(impact).filter(([k, n]) => k !== 'total' && n > 0).map(([k, n]) => <li key={k}>{k.replace(/_/g, ' ')}: <span className="font-mono text-t0">{n}</span></li>)}
            </ul>
          </div>
        ) : (
          <div className="mb-3 p-3 bg-gb border border-gd rounded-lg text-[14px] text-g font-semibold">
            No child rows — safe to purge.
          </div>
        )}
        <div className="text-[14px] text-t2 mb-1">
          Type <b className="text-t0 font-mono">{company.name}</b> to confirm:
        </div>
        <input
          type="text"
          value={text}
          onChange={e => onTextChange(e.target.value)}
          placeholder={company.name}
          className="w-full bg-bg2 border border-b1 rounded-lg px-3 py-2 text-[15px] font-mono text-t0 focus:outline-none focus:border-r mb-3"
          autoFocus
        />
        {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
        <div className="flex gap-2">
          <Bt v="secondary" full onClick={onClose} disabled={busy}>Cancel</Bt>
          <Bt v="danger" full onClick={submit} disabled={busy || !canPurge}>{busy ? 'Purging…' : 'Purge permanently'}</Bt>
        </div>
      </div>
    </div>
  );
};

// Small editable field row for ComplianceSettingsTab — label on the left,
// controlled input on the right. Hoisted to module scope (not defined inside
// ComplianceSettingsTab's render) so its component identity is stable across
// renders: a component redefined on every render makes React unmount/remount
// the <input> on every keystroke, which drops focus out of whatever field you
// were typing in. `cfgs`/`setField` come in as explicit props instead of
// closure captures, since the field's live value still has to track `cfgs`.
const ComplianceField = ({ label, cfgKey, k, unit, type = 'text', placeholder, usedBy, cfgs, setField }) => (
  <div className="flex items-center justify-between gap-2 py-[6px] border-b border-b0">
    <div className="flex-1 min-w-0">
      <label className="text-[14px] text-t1 block">{label}</label>
      {usedBy && <div className="text-[12px] text-t3 mt-[1px]">Read by: <span className="text-p font-semibold">{usedBy}</span></div>}
    </div>
    <div className="flex items-center gap-[4px]">
      <input
        type={type}
        value={cfgs[cfgKey]?.[k] ?? ''}
        placeholder={placeholder || ''}
        onChange={e => setField(cfgKey, k, type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
        className="w-[110px] px-[8px] py-[3px] text-[14px] bg-bg2 border border-b0 rounded text-t0 text-right outline-none focus:border-p"
      />
      {unit && <span className="text-[14px] text-t3 w-[16px]">{unit}</span>}
    </div>
  </div>
);

const ComplianceSettingsTab = ({ initial, onSaved }) => {
  const [cfgs, setCfgs] = React.useState(initial || {});
  const [saving, setSaving] = React.useState(null);
  const [msg, setMsg] = React.useState('');

  // Always reload from the live endpoint so the tab reflects the most
  // recent save even if the parent hasn't refreshed cmpData yet.
  const reload = () => api.get('/api/compliance').then(d => setCfgs(d?.configs || {})).catch(() => {});
  React.useEffect(() => { reload(); }, []); // eslint-disable-line

  const setField = (key, field, val) => setCfgs(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: val } }));
  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3500); };

  const saveCfg = async (key) => {
    setSaving(key);
    try {
      await api.put('/api/compliance/config/' + key, { config_data: cfgs[key] || {} });
      flash(`${key.toUpperCase()} settings saved. Applies to the next payroll run.`);
      notify(`${key.toUpperCase()} settings saved`, { title: 'Saved', variant: 'success' });
      onSaved && onSaved();
      reload();
    } catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save compliance settings', { title: 'Error', variant: 'error' }); }
    finally { setSaving(null); }
  };

  // Small editable field row — label on the left, controlled input on
  // the right. `unit` renders after the field so admin sees "%" or "₹"
  // without having to add it themselves. `usedBy` renders a small pill
  // below the label naming the module that reads the field, so admin
  // never has to wonder "does this actually do anything?"

  // Professional Tax slab editor — one row per state.
  const ptSlabs = cfgs.pt?.slabs || {};
  const setPtSlab = (state, val) => setCfgs(prev => ({
    ...prev,
    pt: { ...(prev.pt || {}), slabs: { ...((prev.pt || {}).slabs || {}), [state]: val === '' ? '' : parseFloat(val) } }
  }));
  const addPtSlab = async () => {
    const s = await promptDialog('State code (e.g. "MH", "KA", "TN"):', { title: 'Add state', placeholder: 'MH' });
    if (!s) return;
    setPtSlab(s.toLowerCase().trim(), 0);
  };
  const removePtSlab = (state) => {
    setCfgs(prev => { const cur = { ...((prev.pt || {}).slabs || {}) }; delete cur[state]; return { ...prev, pt: { ...(prev.pt || {}), slabs: cur } }; });
  };

  return <div>
    <div className="mb-3 p-3 bg-bg2 border border-b0 rounded-lg text-[14px] text-t2">
      <b className="text-t0">What this tab does.</b> These are the statutory rates the payroll module reads when computing deductions. Change them when the government revises a rate — the next payroll run picks up the new values immediately. Every save writes to the audit log with your username. Admin only.
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <Cd>
        <Sec icon={Building} title="Provident Fund (PF)" />
        <div className="text-[14px] text-t3 mb-1">EPFO — Employees' Provident Fund Organisation</div>
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pf" k="er" label="Employer Contribution" unit="%" type="number" placeholder="12" usedBy="Payroll · PF sheet" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pf" k="ee" label="Employee Contribution" unit="%" type="number" placeholder="12" usedBy="Payroll · PF sheet" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pf" k="ceil" label="Wage Ceiling" unit="₹" type="number" placeholder="15000" usedBy="Payroll · PF sheet" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pf" k="admin_charge" label="Admin Charge" unit="%" type="number" placeholder="0.5" usedBy="PF sheet challan total" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pf" k="edli" label="EDLI" unit="%" type="number" placeholder="0.5" usedBy="PF sheet challan total" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pf" k="due_day" label="Due Day (of next month)" unit="" type="number" placeholder="15" usedBy="Compliance filings" />
        <div className="flex items-center gap-2 mt-2"><Bt s="sm" icon={Check} onClick={() => saveCfg('pf')} disabled={saving === 'pf'}>{saving === 'pf' ? 'Saving…' : 'Save PF'}</Bt></div>
      </Cd>

      <Cd>
        <Sec icon={ShieldCheck} title="ESI" />
        <div className="text-[14px] text-t3 mb-1">Employees' State Insurance — auto-applies to gross &lt;= wage ceiling</div>
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="esi" k="er" label="Employer Rate" unit="%" type="number" placeholder="3.25" usedBy="Payroll" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="esi" k="ee" label="Employee Rate" unit="%" type="number" placeholder="0.75" usedBy="Payroll" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="esi" k="wage_ceil" label="Wage Ceiling" unit="₹" type="number" placeholder="21000" usedBy="Payroll" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="esi" k="due_day" label="Due Day" unit="" type="number" placeholder="15" usedBy="Compliance filings" />
        <div className="flex items-center gap-2 mt-2"><Bt s="sm" icon={Check} onClick={() => saveCfg('esi')} disabled={saving === 'esi'}>{saving === 'esi' ? 'Saving…' : 'Save ESI'}</Bt></div>
      </Cd>

      <Cd>
        <Sec icon={DollarSign} title="TDS" />
        <div className="text-[14px] text-t3 mb-1">Income tax deducted at source. Slabs come from the applicable regime.</div>
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="tds" k="due_day" label="Monthly Due Day" unit="th" type="number" placeholder="7" usedBy="Compliance filings" />
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="tds" k="form16_due" label="Form 16 Deadline" unit="" type="text" placeholder="15-Jun" usedBy="Reference (display only)" />
        <div className="flex items-center justify-between gap-2 py-[6px] border-b border-b0">
          <div className="flex-1 min-w-0">
            <label className="text-[14px] text-t1 block">Default Regime</label>
            <div className="text-[12px] text-t3 mt-[1px]">Read by: <span className="text-p font-semibold">New-employee salary template</span></div>
          </div>
          <select value={cfgs.tds?.regime_default || ''} onChange={e => setField('tds', 'regime_default', e.target.value)}
                  className="w-[130px] px-[8px] py-[3px] text-[14px] bg-bg2 border border-b0 rounded text-t0 outline-none">
            <option value="">—</option>
            <option value="new">New (default from FY24-25)</option>
            <option value="old">Old</option>
          </select>
        </div>
        <div className="flex items-center gap-2 mt-2"><Bt s="sm" icon={Check} onClick={() => saveCfg('tds')} disabled={saving === 'tds'}>{saving === 'tds' ? 'Saving…' : 'Save TDS'}</Bt></div>
      </Cd>

      <Cd>
        <Sec icon={DollarSign} title="Professional Tax (State-wise)" />
        <div className="text-[14px] text-t3 mb-1">Monthly deduction per state. Add a row per state your employees are based in.</div>
        <div className="text-[12px] text-p font-semibold mb-2">Read by: Payroll — matches slab against the employee's state on their profile.</div>
        {Object.entries(ptSlabs).length === 0 && <div className="text-[14px] text-t3 py-2">No slabs — add one below.</div>}
        {Object.entries(ptSlabs).map(([st, amt]) => (
          <div key={st} className="flex items-center gap-2 py-[5px] border-b border-b0">
            <span className="text-[14px] font-mono uppercase text-t0 w-[44px]">{st}</span>
            <span className="text-[14px] text-t3">₹</span>
            <input type="number" value={amt ?? ''} onChange={e => setPtSlab(st, e.target.value)}
                   className="flex-1 px-[8px] py-[3px] text-[14px] bg-bg2 border border-b0 rounded text-t0 text-right outline-none focus:border-p" />
            <span className="text-[14px] text-t3">/mo</span>
            <button type="button" onClick={() => removePtSlab(st)} className="text-r bg-transparent border-0 cursor-pointer p-[2px]" title="Remove"><X size={12} /></button>
          </div>
        ))}
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="pt" k="due_day" label="Due Day" unit="th" type="number" placeholder="28" usedBy="Compliance filings" />
        <div className="flex items-center gap-2 mt-2">
          <Bt s="sm" icon={Plus} v="secondary" onClick={addPtSlab}>Add State</Bt>
          <Bt s="sm" icon={Check} onClick={() => saveCfg('pt')} disabled={saving === 'pt'}>{saving === 'pt' ? 'Saving…' : 'Save PT'}</Bt>
        </div>
      </Cd>

      <Cd>
        <Sec icon={Award} title="Gratuity" />
        <div className="text-[14px] text-t3 mb-1">Payment of Gratuity Act — vests after min tenure of continuous service.</div>
        <ComplianceField cfgs={cfgs} setField={setField} cfgKey="gratuity" k="min_years" label="Min Years" unit="yr" type="number" placeholder="5" usedBy="Separation · F&F" />
        <div className="flex items-center justify-between gap-2 py-[6px] border-b border-b0">
          <div className="flex-1 min-w-0">
            <label className="text-[14px] text-t1 block">Formula</label>
            <div className="text-[12px] text-t3 mt-[1px]">Reference only — actual formula is picked in Settings &gt; Separation.</div>
          </div>
          <select value={cfgs.gratuity?.formula || ''} onChange={e => setField('gratuity', 'formula', e.target.value)}
                  className="w-[220px] px-[8px] py-[3px] text-[14px] bg-bg2 border border-b0 rounded text-t0 outline-none">
            <option value="">—</option>
            <option value="last_salary_x_15_x_years/26">last_salary × 15 × years / 26 (standard)</option>
            <option value="custom">Custom (see payroll config)</option>
          </select>
        </div>
        <div className="flex items-center justify-between gap-2 py-[6px] border-b border-b0">
          <div className="flex-1 min-w-0">
            <label className="text-[14px] text-t1 block">Provision</label>
            <div className="text-[12px] text-t3 mt-[1px]">Reference only today.</div>
          </div>
          <select value={cfgs.gratuity?.provision || ''} onChange={e => setField('gratuity', 'provision', e.target.value)}
                  className="w-[180px] px-[8px] py-[3px] text-[14px] bg-bg2 border border-b0 rounded text-t0 outline-none">
            <option value="">—</option>
            <option value="monthly">Monthly (accrue each cycle)</option>
            <option value="on_exit">On exit only</option>
          </select>
        </div>
        <div className="flex items-center gap-2 mt-2"><Bt s="sm" icon={Check} onClick={() => saveCfg('gratuity')} disabled={saving === 'gratuity'}>{saving === 'gratuity' ? 'Saving…' : 'Save Gratuity'}</Bt></div>
      </Cd>
    </div>

    {msg && <div className={`mt-3 px-3 py-2 rounded-lg text-[14px] font-semibold ${msg.startsWith('Error') ? 'bg-rb text-r border border-rd' : 'bg-gb text-g border border-gd'}`}>{msg}</div>}
  </div>;
};

// Security > Login rate limit. Admin controls how many attempts an IP
// can make in a rolling window before being blocked with 429. Change
// takes effect within ~30 seconds without a server restart (rateLimit
// middleware polls compliance_config.login_config).
const SecuritySettingsTab = () => {
  const [cfg, setCfg] = React.useState(null);
  const [max, setMax] = React.useState('');
  const [win, setWin] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const load = () => api.get('/api/settings/login').then(d => {
    setCfg(d);
    setMax(String(d.max_attempts));
    setWin(String(d.window_min));
  }).catch(() => setCfg({ max_attempts: 10, window_min: 15, defaults: { max_attempts: 10, window_min: 15 } }));
  React.useEffect(() => { load(); }, []);

  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3500); };

  const save = async () => {
    const m = parseInt(max, 10);
    const w = parseInt(win, 10);
    if (!(m >= 3 && m <= 50))   { flash('Max attempts must be between 3 and 50.', true); return; }
    if (!(w >= 1 && w <= 60))   { flash('Window must be between 1 and 60 minutes.', true); return; }
    setSaving(true);
    try {
      const d = await api.put('/api/settings/login', { max_attempts: m, window_min: w });
      setCfg(prev => ({ ...prev, ...d }));
      flash('Saved. Change takes effect within 30 seconds.');
      notify('Security settings saved', { title: 'Saved', variant: 'success' });
    } catch (e) { flash(e.message || 'Save failed', true); notify(e.message || 'Failed to save security settings', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-[14px] text-t3">Loading…</div>;
  return <div className="max-w-[720px]">
    <div className="text-[14px] font-bold text-t0 mb-1">Login Attempts</div>
    <div className="text-[14px] text-t3 mb-3">
      Controls how many sign-in attempts a single device (IP) can make before it's temporarily blocked with a "too many attempts" message. Applies to every sign-in on every device — successful and failed both count. Password reset stays with Admin either way; the limit doesn't lock the account itself.
    </div>
    <Cd>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[12px] text-t3 mb-[3px] uppercase font-semibold">Max attempts allowed</div>
          <Inp value={max} onChange={setMax} placeholder="10" />
          <div className="text-[12px] text-t3 mt-[3px]">Allowed range: 3 – 50. Default: {cfg.defaults?.max_attempts ?? 10}.</div>
        </div>
        <div>
          <div className="text-[12px] text-t3 mb-[3px] uppercase font-semibold">Window (minutes)</div>
          <Inp value={win} onChange={setWin} placeholder="15" />
          <div className="text-[12px] text-t3 mt-[3px]">Allowed range: 1 – 60. Default: {cfg.defaults?.window_min ?? 15}.</div>
        </div>
      </div>
      <div className="mt-3 p-2 bg-bg2 rounded-lg border border-b0 text-[14px] text-t2">
        Current setting: <b className="text-t0">{cfg.max_attempts} attempts per {cfg.window_min} minute{cfg.window_min === 1 ? '' : 's'}</b> per device. Once tripped, sign-in unblocks automatically after the window resets — the employee should be told to wait, or contact HR for a password reset.
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Bt icon={CheckCircle} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Security Settings'}</Bt>
        {msg && <span className={`text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</span>}
      </div>
    </Cd>
  </div>;
};

// Every hard delete anywhere in the app writes a row here via
// server/utils/deletionLog.js's logDeletion() — this tab is the read side.
// Separate from the older free-text audit_log (which mixes every action
// together with no module/entity structure).
const DELETION_MODULE_LABELS = { onboarding: "Onboarding", separation: "Offboarding", employees: "Employees", assets: "Assets", recruitment: "Recruitment", leaves: "Leaves", appraisal: "Appraisal", payroll: "Payroll", expenses: "Expenses", documents: "Policies", compliance: "Compliance", helpdesk: "Helpdesk", org: "Organization", access_control: "Access Control", permissions: "Permissions", automation: "Automation", vendors: "Vendors", templates: "Email/Form Templates", announcements: "Announcements", events: "Events", dashboard: "Dashboard" };

const DeletionLogTab = () => {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (moduleFilter !== "all") params.set("module", moduleFilter);
    if (search.trim()) params.set("search", search.trim());
    api.get(`/api/audit-log/deletions?${params.toString()}`)
      .then((d) => setRows(Array.isArray(d) ? d : d.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [moduleFilter]); // eslint-disable-line
  useEffect(() => { api.get('/api/audit-log/deletions/modules').then((d) => setModules(Array.isArray(d) ? d : [])).catch(() => setModules([])); }, []);

  const fmtWhen = (ts) => { try { return new Date(ts).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ts; } };

  return (
    <div>
      <div className="text-[14px] font-bold text-t0 mb-1">Deletion Log</div>
      <div className="text-[14px] text-t3 mb-3">Every hard delete across the app, in one place — what was deleted, from which module, by whom, and when.</div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="w-[220px]">
          <Sl options={[{ v: "all", l: "All modules" }, ...modules.map((m) => ({ v: m, l: DELETION_MODULE_LABELS[m] || m }))]} value={moduleFilter} onChange={setModuleFilter} />
        </div>
        <div className="flex-1 min-w-[220px] flex items-center gap-2">
          <Inp value={search} onChange={setSearch} placeholder="Search what was deleted, or who deleted it…" />
        </div>
        <Bt v="secondary" s="sm" onClick={load}>Search</Bt>
      </div>

      {loading ? (
        <div className="text-[14px] text-t3 py-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[14px] text-t3 py-6 text-center">No deletions match this filter.</div>
      ) : (
        <Tbl
          cols={[
            { k: "when", l: "When", r: (r) => <span className="text-[13px] text-t2 whitespace-nowrap">{fmtWhen(r.created_at)}</span> },
            { k: "module", l: "Module", r: (r) => <Bg c={K.p} sm>{DELETION_MODULE_LABELS[r.module] || r.module}</Bg> },
            { k: "what", l: "What was deleted", r: (r) => <span className="text-[14px] text-t0">{r.entity_label || `${r.entity_type} #${r.entity_id}`}<span className="text-t3 text-[12px]"> ({r.entity_type})</span></span> },
            { k: "by", l: "Deleted by", r: (r) => <span className="text-[14px] text-t1">{r.deleted_by_name || r.deleted_by_username || "—"}</span> },
          ]}
          data={rows}
        />
      )}
    </div>
  );
};
